document.documentElement.classList.remove('no-js');
document.documentElement.classList.add('js');

/* Shared geometry for Apple-like morph transitions. The panel always opens
   from the real trigger rectangle and closes toward the user's actual exit
   point (button centre, backdrop tap, or drag release). */
const rbMotionSourceElement = (source) => {
  if (source instanceof Element) return source;
  return source?.target instanceof Element ? source.target.closest('button,a,[role="button"],summary') || source.target : null;
};

const rbSetMorphTarget = (layer, panel, source, fallbackSource = null) => {
  if (!layer || !panel) return;
  const sourceElement = rbMotionSourceElement(source) || rbMotionSourceElement(fallbackSource);
  const isBackdrop = sourceElement?.matches?.('[class*="backdrop"]');
  const sourceRect = sourceElement && !isBackdrop ? sourceElement.getBoundingClientRect() : null;
  const eventX = Number.isFinite(source?.clientX) && (source.clientX || source.clientY) ? source.clientX : null;
  const eventY = Number.isFinite(source?.clientY) && (source.clientX || source.clientY) ? source.clientY : null;
  const pointX = eventX ?? (sourceRect ? sourceRect.left + sourceRect.width / 2 : window.innerWidth / 2);
  const pointY = eventY ?? (sourceRect ? sourceRect.top + sourceRect.height / 2 : window.innerHeight / 2);

  const previousTransition = panel.style.transition;
  const previousTransform = panel.style.transform;
  panel.style.transition = 'none';
  panel.style.transform = 'none';
  const panelRect = panel.getBoundingClientRect();
  panel.style.transition = previousTransition;
  panel.style.transform = previousTransform;

  const ratio = sourceRect
    ? Math.min(sourceRect.width / Math.max(panelRect.width, 1), sourceRect.height / Math.max(panelRect.height, 1))
    : .22;
  const scale = Math.max(.18, Math.min(.96, ratio || .22));
  const radius = sourceRect ? getComputedStyle(sourceElement).borderRadius || '28px' : '999px';
  layer.style.setProperty('--rb-motion-x', `${pointX - (panelRect.left + panelRect.width / 2)}px`);
  layer.style.setProperty('--rb-motion-y', `${pointY - (panelRect.top + panelRect.height / 2)}px`);
  layer.style.setProperty('--rb-motion-scale', String(scale));
  layer.style.setProperty('--rb-motion-radius', radius === '0px' ? '28px' : radius);
};

window.RUBYST_MOTION = { setTarget: rbSetMorphTarget };

/* Shopify Inbox now places a guest-choice gate in the chat runtime. If the
   storefront only clicks the outer activator, the drawer can look blank even
   though the composer has already loaded behind that gate. Keep the original
   shopper gesture alive until the runtime is open, then choose Shopify's own
   "continue without signing in" path when a brand-new guest chat requires it.
   Re-opening an initialized conversation does not start a new thread. */
let rbShopifyChatOpening = null;
let rbShopifyChatOpeningToken = 0;
let rbShopifyAgentIframeReady = false;
let rbShopifyReturnedFromPageCache = false;
const rbShopifyChatRuntime = () => document.querySelector('shopify-chat')
  ?.shadowRoot?.querySelector('shopify-agent-runtime');

window.addEventListener('pageshow', (event) => {
  if (event.persisted) rbShopifyReturnedFromPageCache = true;
});

/* The connection hint is allowed to paint only after Shopify has mounted its
   real dialog. On phones the compact native-viewport style is already active,
   so the hint can never inherit the old 1280px loading-card dimensions. */
const rbMarkShopifyChatMounted = (runtime = rbShopifyChatRuntime()) => {
  const root = document.documentElement;
  const panel = runtime?.shadowRoot?.querySelector('[part="dialog"]');
  if (!runtime?.hasAttribute('open') || !panel) return false;
  root.classList.add('support-chat-has-native-shell');
  return true;
};

/* The outer buyer-claims iframe becomes cross-origin before Shopify has
   hydrated the actual conversation. Waiting for Shopify's own ready message
   prevents Android shoppers from seeing a blank white sheet during that gap. */
window.addEventListener('message', (event) => {
  if (event.origin !== 'https://storefront-agent-server.shopify.ai') return;
  if (event.data?.type !== 'shopify-agent:iframe-ready') return;
  rbShopifyAgentIframeReady = true;
});

/* Shopify chooses its iframe layout at creation time. Phones keep Inbox on a
   true device-resolution viewport while the conversation is open, which keeps
   the cross-origin text sharp. Geometry follows VisualViewport so the browser
   keyboard cannot push the close control over messages or the composer. */
let rbChatBaseVisualHeight = 0;

const rbChatViewportGeometry = () => {
  const root = document.documentElement;
  const visual = window.visualViewport;
  const left = 0;
  const width = Math.max(280, visual?.width || window.innerWidth);
  const measuredHeight = Math.max(260, visual?.height || window.innerHeight);
  const headerHeight = 42;

  if (window.innerWidth <= 560) rbChatBaseVisualHeight = Math.max(rbChatBaseVisualHeight, measuredHeight);
  const runtime = rbShopifyChatRuntime();
  const agentFrame = runtime?.shadowRoot?.querySelector('iframe');
  const composerFocused = Boolean(agentFrame && (
    runtime.shadowRoot?.activeElement === agentFrame
    || agentFrame.matches(':focus')
  ));
  const viewportReduced = rbChatBaseVisualHeight > 0 && measuredHeight < rbChatBaseVisualHeight * .72;
  const keyboardOpen = viewportReduced && composerFocused;
  /* Keep Inbox at its complete full-screen height and align its bottom edge
     with the visible viewport instead of shrinking the dialog. WebKit can keep
     a stale keyboard height/offset after the keyboard disappears; a constant
     dialog height means that stale value can move only the hidden top edge and
     can never leave the conversation stranded in the lower half of the page. */
  const visualTop = Math.max(0, Number(visual?.offsetTop) || 0);
  const height = Math.max(measuredHeight, rbChatBaseVisualHeight);
  const visibleBottom = visualTop + measuredHeight;
  const panelTop = Math.min(headerHeight, visibleBottom - height + headerHeight);
  const top = 0;
  root.classList.toggle('rb-chat-keyboard-open', keyboardOpen);
  root.style.setProperty('--rb-chat-vv-left', `${left}px`);
  root.style.setProperty('--rb-chat-vv-top', `${top}px`);
  root.style.setProperty('--rb-chat-vv-width', `${width}px`);
  root.style.setProperty('--rb-chat-vv-height', `${height}px`);
  root.style.setProperty('--rb-chat-header-height', `${headerHeight}px`);
  return { left, top, width, height, headerHeight, panelTop, keyboardOpen };
};

const rbResetChatViewportGeometry = () => {
  const root = document.documentElement;
  rbChatBaseVisualHeight = 0;
  root.classList.remove('rb-chat-keyboard-open');
  ['--rb-chat-vv-left', '--rb-chat-vv-top', '--rb-chat-vv-width', '--rb-chat-vv-height', '--rb-chat-header-height']
    .forEach((property) => root.style.removeProperty(property));
};

const rbFitShopifyChatToPhone = (runtime = rbShopifyChatRuntime()) => {
  const root = document.documentElement;
  if (!root.classList.contains('support-chat-is-open')) return;
  if (root.classList.contains('support-chat-is-closing')) return;
  const phoneNative = root.classList.contains('phone-readable-type')
    && root.classList.contains('rb-chat-native-viewport');
  const viewport = phoneNative ? rbChatViewportGeometry() : null;
  const panel = runtime?.shadowRoot?.querySelector('[part="dialog"]');
  if (!panel) return;
  runtime.shadowRoot?.querySelectorAll('.panel__size-toggle,.panel__close').forEach((control) => {
    control.style.setProperty('display', 'none', 'important');
  });
  if (!root.classList.contains('phone-readable-type')) {
    panel.style.removeProperty('transform');
    panel.style.removeProperty('transform-origin');
    panel.style.removeProperty('zoom');
    panel.style.removeProperty('visibility');
    panel.style.removeProperty('opacity');
    panel.style.removeProperty('pointer-events');
    return;
  }

  /* Keep Inbox at a genuine mobile layout while it is open. Scaling a
     cross-origin iframe with transform makes Android rasterize the glyphs
     before enlargement, which is the source of the fuzzy chat text. */
  if (phoneNative && viewport) {
    panel.style.setProperty('position', 'fixed', 'important');
    panel.style.setProperty('left', `${viewport.left}px`, 'important');
    panel.style.setProperty('right', 'auto', 'important');
    panel.style.setProperty('top', `${viewport.panelTop}px`, 'important');
    panel.style.setProperty('bottom', 'auto', 'important');
    panel.style.setProperty('width', `${viewport.width}px`, 'important');
    panel.style.setProperty('height', `${Math.max(240, viewport.height - viewport.headerHeight)}px`, 'important');
    panel.style.setProperty('max-width', 'none', 'important');
    panel.style.setProperty('max-height', 'none', 'important');
    panel.style.setProperty('margin', '0', 'important');
    panel.style.setProperty('overflow', 'hidden', 'important');
    panel.style.setProperty('zoom', '1', 'important');
    panel.style.setProperty('transform', 'none', 'important');
    panel.style.setProperty('transform-origin', '0 100%', 'important');
    panel.style.setProperty('animation', 'none', 'important');
    panel.style.setProperty('transition', 'none', 'important');
    panel.style.setProperty('visibility', 'visible', 'important');
    panel.style.setProperty('opacity', '1', 'important');
    panel.style.setProperty('pointer-events', 'auto', 'important');
    panel.style.setProperty('border', '0', 'important');
    panel.style.setProperty('border-radius', '0', 'important');
    panel.style.setProperty('box-shadow', 'none', 'important');
    return;
  }

  const screenWidth = Math.min(window.screen?.width || 390, window.screen?.height || 844);
  const dialogWidth = Math.max(320, Math.min(520, Math.round(screenWidth)));
  const compensation = window.innerWidth / dialogWidth;
  const physicalHeight = window.innerHeight / compensation;
  const dialogHeight = Math.max(360, Math.round(physicalHeight * .82));

  panel.style.setProperty('position', 'fixed', 'important');
  panel.style.setProperty('left', '0', 'important');
  panel.style.setProperty('right', 'auto', 'important');
  panel.style.setProperty('top', 'auto', 'important');
  panel.style.setProperty('bottom', '0', 'important');
  panel.style.setProperty('width', `${dialogWidth}px`, 'important');
  panel.style.setProperty('height', `${dialogHeight}px`, 'important');
  panel.style.setProperty('max-width', 'none', 'important');
  panel.style.setProperty('max-height', 'none', 'important');
  panel.style.setProperty('margin', '0', 'important');
  panel.style.setProperty('overflow', 'hidden', 'important');
  panel.style.setProperty('zoom', '1', 'important');
  panel.style.setProperty('transform', `scale(${compensation})`, 'important');
  panel.style.setProperty('transform-origin', '0 100%', 'important');
  panel.style.setProperty('animation', 'none', 'important');
  panel.style.setProperty('transition', 'none', 'important');
  panel.style.setProperty('visibility', 'visible', 'important');
  panel.style.setProperty('opacity', '1', 'important');
  panel.style.setProperty('backface-visibility', 'visible', 'important');
  panel.style.setProperty('will-change', 'auto', 'important');
  panel.style.setProperty('border-radius', '22px 22px 0 0', 'important');
};

const rbShopifyGuestChatState = (runtime) => {
  const shadow = runtime?.shadowRoot;
  const panel = shadow?.querySelector('[part="dialog"]');
  const continueCandidate = shadow?.querySelector('.new-chat-popover__continue');
  const continueButton = continueCandidate?.getClientRects().length ? continueCandidate : null;
  const newChatCandidate = shadow?.querySelector('.panel__new-chat,.panel__history-new-chat');
  const newChatControl = newChatCandidate?.shadowRoot?.querySelector('button') || newChatCandidate;
  const newChatRect = newChatCandidate?.getBoundingClientRect();
  const newChatButton = newChatCandidate
    && !newChatCandidate.hasAttribute('visually-hidden')
    && newChatRect?.width > 4
    && newChatRect?.height > 4
    ? newChatControl
    : null;
  if (!panel) return { panel: null, continueButton: null, newChatButton: null, hasActiveView: false };

  /* panel--empty-state is Shopify's sign-in/guest-choice screen, not a live
     conversation. Do not mark it ready until that choice has been completed. */
  const hasActiveView = [...panel.classList].some((name) =>
    !['panel', 'panel--right', 'panel--left', 'panel--empty-state'].includes(name)
  );
  return { panel, continueButton, newChatButton, hasActiveView };
};

/* Shopify's outer runtime can report itself as open even when WebKit blocks
   /agent/buyer-claims at the redirect CSP. In that failure mode the iframe
   stays same-origin on a blank/404 document. A working conversation crosses
   to storefront-agent-server.shopify.ai, so reading its location throws the
   expected cross-origin SecurityError. The iframe-ready postMessage is useful
   on a first load, but Safari does not reliably replay it after a BFCache
   restore; the verified cross-origin handoff is therefore also sufficient. */
const rbShopifyConversationReady = (runtime = rbShopifyChatRuntime()) => {
  const frame = runtime?.shadowRoot?.querySelector('iframe[src*="/agent/buyer-claims"]');
  if (!frame?.contentWindow) return false;
  try {
    const currentUrl = frame.contentWindow.location.href;
    return rbShopifyAgentIframeReady && /^https:\/\/storefront-agent-server\.shopify\.ai\//i.test(currentUrl);
  } catch (error) {
    return error?.name === 'SecurityError' || /cross-origin|denied|blocked/i.test(String(error?.message || error));
  }
};

window.RUBYST_SHOPIFY_CHAT_READY = rbShopifyConversationReady;

const rbOpenShopifyChat = () => {
  if (rbShopifyChatOpening) return rbShopifyChatOpening;

  const openingToken = ++rbShopifyChatOpeningToken;
  const openingPromise = new Promise((resolve, reject) => {
    const startedAt = performance.now();
    const phoneBootstrap = document.documentElement.classList.contains('phone-readable-type');
    let activatorClicked = false;
    let nextRuntimeOpenAt = 0;
    let nextGuestContinueAt = 0;
    let nextNewChatAt = 0;
    let conversationStableSince = 0;
    let runtimeOpenSince = 0;
    let runtimeRecoveryAttempts = 0;
    if (phoneBootstrap) window.RUBYST_SET_CHAT_BOOT_VIEWPORT?.(true);
    const openWhenReady = () => {
      if (openingToken !== rbShopifyChatOpeningToken) {
        reject(new Error('Shopify chat opening was superseded.'));
        return;
      }
      const host = document.querySelector('shopify-chat');
      const runtime = rbShopifyChatRuntime();
      if (phoneBootstrap && (window.innerWidth > 560 || (window.visualViewport?.scale || 0) < .8)) {
        if (performance.now() - startedAt < 1600) {
          window.setTimeout(openWhenReady, 24);
          return;
        }
      }
      rbFitShopifyChatToPhone(runtime);
      rbMarkShopifyChatMounted(runtime);
      if (runtime?.hasAttribute('open')) {
        if (!runtimeOpenSince) runtimeOpenSince = performance.now();
        const agentFrame = runtime.shadowRoot?.querySelector('iframe[src*="/agent/buyer-claims"]');
        const recoveryDelay = rbShopifyReturnedFromPageCache ? 1800 : 6500;
        if (!agentFrame
          && runtimeRecoveryAttempts < 1
          && performance.now() - runtimeOpenSince >= recoveryDelay) {
          runtimeRecoveryAttempts += 1;
          runtimeOpenSince = 0;
          runtime.close?.();
          window.setTimeout(() => {
            if (openingToken !== rbShopifyChatOpeningToken) return;
            const currentRuntime = rbShopifyChatRuntime();
            if (typeof currentRuntime?.openFromActivator === 'function') currentRuntime.openFromActivator();
            else if (typeof host?.show === 'function') host.show();
            else currentRuntime?.setAttribute('open', '');
            openWhenReady();
          }, 180);
          return;
        }
        const guestState = rbShopifyGuestChatState(runtime);
        if (!guestState.continueButton && rbShopifyConversationReady(runtime)) {
          if (!conversationStableSince) conversationStableSince = performance.now();
          const readyDelay = rbShopifyAgentIframeReady ? 700 : 3200;
          if (performance.now() - conversationStableSince >= readyDelay) {
            rbShopifyReturnedFromPageCache = false;
            resolve(runtime);
            return;
          }
        } else {
          conversationStableSince = 0;
        }
        if (guestState.continueButton && performance.now() >= nextGuestContinueAt) {
          nextGuestContinueAt = performance.now() + 450;
          guestState.continueButton.click();
        } else if (!guestState.hasActiveView && guestState.newChatButton && performance.now() >= nextNewChatAt) {
          nextNewChatAt = performance.now() + 600;
          guestState.newChatButton.click();
        }
        /* The Shopify agent iframe and its guest gate arrive on separate
           network turns. Keep polling until the runtime exposes its active
           view instead of declaring success at the first outer panel paint. */
      } else {
        runtimeOpenSince = 0;
      }

      const activator = host?.shadowRoot?.querySelector('[part="activator"]');
      if (!runtime?.hasAttribute('open') && activator && !activatorClicked) {
        activatorClicked = true;
        activator.click();
      } else if (!runtime?.hasAttribute('open') && runtime && !activator && performance.now() >= nextRuntimeOpenAt) {
        /* Shopify removes its outer activator after the first conversation.
           Trigger the surviving runtime's own activator so its private state
           and event pipeline are restored on Chromium as well as WebKit.
           Retry gently while Shopify finishes its own close work. */
        nextRuntimeOpenAt = performance.now() + 450;
        const runtimeActivator = runtime.shadowRoot?.querySelector('button.activator');
        if (runtimeActivator) runtimeActivator.click();
        else if (typeof runtime.openFromActivator === 'function') runtime.openFromActivator();
        else if (typeof runtime.show === 'function') runtime.show();
        else runtime.setAttribute('open', '');
      }

      if (performance.now() - startedAt < 18000) {
        window.setTimeout(openWhenReady, 60);
      } else {
        reject(new Error('Shopify chat did not become ready.'));
      }
    };
    openWhenReady();
  });
  const trackedOpening = openingPromise.finally(() => {
    if (openingToken === rbShopifyChatOpeningToken && rbShopifyChatOpening === trackedOpening) {
      rbShopifyChatOpening = null;
    }
  });
  rbShopifyChatOpening = trackedOpening;
  return rbShopifyChatOpening;
};

const rbCancelShopifyChatOpening = () => {
  rbShopifyChatOpeningToken += 1;
  rbShopifyChatOpening = null;
};

window.RUBYST_OPEN_SHOPIFY_CHAT = rbOpenShopifyChat;

/* One storefront support controller for home and product pages. The first
   shopper gesture opens Shopify Inbox itself; the theme never places an
   interstitial card above the real guest gate or conversation composer. */
let rbSupportOpenRequest = 0;
let rbSupportRuntimeObserver = null;
let rbObservedSupportRuntime = null;
let rbSupportReturnFocus = null;
let rbSupportReturnScroll = { x: 0, y: 0 };
let rbSupportIsClosing = false;
let rbSupportIsOpening = false;
let rbSupportQueuedOpen = null;
let rbSupportViewportTimer = 0;

const rbChatResetParameter = '_rb_chat_reset';
const rbChatReloadStateKey = 'rubyst-chat-reload-return-v1';
const rbRestoreChatReloadState = () => {
  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has(rbChatResetParameter)) return;
    url.searchParams.delete(rbChatResetParameter);
    const cleanLocation = `${url.pathname}${url.search}${url.hash}`;
    window.history.replaceState(window.history.state, '', cleanLocation);
    const saved = JSON.parse(window.sessionStorage.getItem(rbChatReloadStateKey) || 'null');
    window.sessionStorage.removeItem(rbChatReloadStateKey);
    if (!saved || saved.location !== cleanLocation || Date.now() - saved.savedAt > 12000) return;
    const restore = () => window.scrollTo(saved.x || 0, saved.y || 0);
    requestAnimationFrame(() => requestAnimationFrame(restore));
    window.setTimeout(restore, 160);
    window.setTimeout(restore, 480);
  } catch (error) {}
};

rbRestoreChatReloadState();

const rbSupportRoot = document.documentElement;
rbSupportRoot.classList.add('rb-shopify-inbox-custom');
const rbSupportShell = () => document.querySelector('[data-support-chat-shell]');

const rbWatchSupportViewport = () => {
  window.clearTimeout(rbSupportViewportTimer);
  const keepFitted = () => {
    if (!rbSupportRoot.classList.contains('support-chat-is-open')) {
      rbSupportViewportTimer = 0;
      return;
    }
    rbFitShopifyChatToPhone();
    rbSupportViewportTimer = window.setTimeout(keepFitted, 80);
  };
  keepFitted();
};

const rbQueryOpenShadow = (node, predicate) => {
  if (!node) return null;
  const children = node instanceof ShadowRoot || node instanceof DocumentFragment || node instanceof Element
    ? [...node.children]
    : [];
  for (const child of children) {
    if (predicate(child)) return child;
    const shadowMatch = rbQueryOpenShadow(child.shadowRoot, predicate);
    if (shadowMatch) return shadowMatch;
    const lightMatch = rbQueryOpenShadow(child, predicate);
    if (lightMatch) return lightMatch;
  }
  return null;
};

const rbSetSupportState = (open) => {
  const shell = rbSupportShell();
  rbSupportRoot.classList.toggle('support-chat-is-open', open);
  if (!open) {
    window.clearTimeout(rbSupportViewportTimer);
    rbSupportViewportTimer = 0;
    rbSupportRoot.classList.remove('support-chat-is-ready', 'support-chat-load-failed', 'support-chat-has-native-shell');
    window.RUBYST_SET_CHAT_BOOT_VIEWPORT?.(false);
  } else {
    rbWatchSupportViewport();
  }
  shell?.setAttribute('aria-hidden', String(!open));
  document.querySelectorAll('[data-support-trigger],[data-phone-support],[data-rb-product-support],.phone-control-support')
    .forEach((button) => button.setAttribute('aria-expanded', String(open)));
};

const rbWatchSupportRuntime = (runtime) => {
  if (!runtime || runtime === rbObservedSupportRuntime) return;
  rbSupportRuntimeObserver?.disconnect();
  rbObservedSupportRuntime = runtime;
  rbSupportRuntimeObserver = new MutationObserver(() => {
    if (runtime.hasAttribute('open')) return;
    if (!rbSupportRoot.classList.contains('support-chat-is-open')) return;
    if (rbSupportIsClosing || rbSupportIsOpening) return;
    const requestId = ++rbSupportOpenRequest;
    rbSupportIsOpening = true;
    rbSupportRoot.classList.remove('support-chat-is-ready', 'support-chat-load-failed');
    rbConnectSupport(requestId);
  });
  rbSupportRuntimeObserver.observe(runtime, { attributes: true, attributeFilter: ['open'] });
};

/* Reveal Shopify's real surface as soon as its runtime opens. The guest
   choice and every message composer belong to Inbox, so nothing that looks
   like a conversation is simulated by the theme. */
const rbRevealNativeChat = (requestId) => {
  const startedAt = performance.now();
  let conversationStableSince = 0;
  const revealWhenOpen = () => {
    if (requestId !== rbSupportOpenRequest) return;
    const runtime = rbShopifyChatRuntime();
    rbFitShopifyChatToPhone(runtime);
    rbMarkShopifyChatMounted(runtime);
    rbWatchSupportRuntime(runtime);
    const guestState = rbShopifyGuestChatState(runtime);
    if (runtime?.hasAttribute('open') && !guestState.continueButton && rbShopifyConversationReady(runtime)) {
      if (!conversationStableSince) conversationStableSince = performance.now();
      const readyDelay = rbShopifyAgentIframeReady ? 700 : 3200;
      if (performance.now() - conversationStableSince >= readyDelay) {
        rbSupportRoot.classList.add('support-chat-is-ready');
        rbSupportIsOpening = false;
        return;
      }
    } else {
      conversationStableSince = 0;
    }
    if (performance.now() - startedAt < 18000) window.setTimeout(revealWhenOpen, 60);
  };
  revealWhenOpen();
};

const rbCloseSupport = () => {
  if (rbSupportIsClosing) return;
  rbSupportIsClosing = true;
  rbSupportIsOpening = false;
  rbSupportOpenRequest += 1;
  rbCancelShopifyChatOpening();
  const restoringPhoneViewport = rbSupportRoot.classList.contains('phone-readable-type')
    && rbSupportRoot.classList.contains('rb-chat-native-viewport');
  rbSupportRoot.classList.add('support-chat-is-closing');
  rbSupportRoot.classList.remove('rb-chat-viewport-ready');
  const host = document.querySelector('shopify-chat');
  const runtime = rbShopifyChatRuntime();
  const panel = runtime?.shadowRoot?.querySelector('[part="dialog"]');
  const closeHost = runtime?.shadowRoot?.querySelector('shopify-chat-icon-button[name="close"],.panel__close');
  const closeButton = closeHost?.shadowRoot?.querySelector('button')
    || rbQueryOpenShadow(host?.shadowRoot, (element) => element.matches?.('button[aria-label*="close" i]'));

  if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
  runtime?.shadowRoot?.activeElement?.blur?.();
  runtime?.shadowRoot?.querySelector('iframe')?.blur?.();
  document.querySelector('.rb-support-chat-close')?.focus?.({ preventScroll: true });
  panel?.style.setProperty('pointer-events', 'none', 'important');

  const closeRuntime = () => {
    try {
      if (typeof runtime?.close === 'function') runtime.close();
      else (closeButton || closeHost)?.click();
    } catch (error) {
      runtime?.removeAttribute?.('open');
    }
  };

  const completeClose = () => {
    panel?.style.setProperty('visibility', 'hidden', 'important');
    panel?.style.setProperty('opacity', '0', 'important');
    closeRuntime();
    rbSetSupportState(false);
    rbSupportRoot.classList.remove(
      'rb-chat-closing-viewport',
      'rb-chat-viewport-ready',
      'support-chat-is-closing'
    );
    rbResetChatViewportGeometry();
    window.RUBYST_PIN_PHONE_VIEWPORT?.();
    window.scrollTo(rbSupportReturnScroll.x, rbSupportReturnScroll.y);
    window.RUBYST_FLOATING_CART?.reset?.();
    requestAnimationFrame(() => {
      window.RUBYST_PIN_PHONE_VIEWPORT?.();
      window.scrollTo(rbSupportReturnScroll.x, rbSupportReturnScroll.y);
      window.RUBYST_FLOATING_CART?.reset?.();
    });
    rbSupportReturnFocus?.focus?.({ preventScroll: true });
    rbSupportIsClosing = false;
    const queuedSource = rbSupportQueuedOpen;
    rbSupportQueuedOpen = null;
    if (queuedSource) rbOpenSupport(queuedSource);
  };

  window.clearTimeout(rbSupportViewportTimer);
  rbSupportViewportTimer = 0;
  if (restoringPhoneViewport) window.RUBYST_PIN_PHONE_VIEWPORT?.(true);
  completeClose();
};

const rbConnectSupport = (requestId) => {
  rbRevealNativeChat(requestId);

  Promise.resolve(window.RUBYST_OPEN_SHOPIFY_CHAT?.()).then((runtime) => {
    rbWatchSupportRuntime(runtime);
    const guestState = rbShopifyGuestChatState(runtime);
    if (requestId === rbSupportOpenRequest
      && runtime?.hasAttribute('open')
      && !guestState.continueButton
      && rbShopifyConversationReady(runtime)) {
      rbSupportRoot.classList.add('support-chat-is-ready');
      rbSupportIsOpening = false;
    }
  }).catch(() => {
    if (requestId === rbSupportOpenRequest) {
      rbSupportIsOpening = false;
      if (!rbSupportRoot.classList.contains('support-chat-is-ready')) {
        rbSupportRoot.classList.add('support-chat-load-failed');
      }
    }
  });
};

const rbOpenSupport = (source = null) => {
  if (rbSupportIsClosing) {
    rbSupportQueuedOpen = rbMotionSourceElement(source) || true;
    return;
  }
  if (rbSupportRoot.classList.contains('support-chat-is-open')) return;
  rbSupportRoot.classList.remove('rb-chat-closing-viewport', 'rb-chat-viewport-ready');
  rbResetChatViewportGeometry();
  rbSupportReturnFocus = rbMotionSourceElement(source);
  rbSupportReturnScroll = { x: window.scrollX, y: window.scrollY };
  rbSupportIsOpening = true;
  const requestId = ++rbSupportOpenRequest;
  rbSupportRoot.classList.remove('support-chat-is-ready', 'support-chat-load-failed', 'support-chat-has-native-shell');
  rbSetSupportState(true);
  if (rbSupportRoot.classList.contains('phone-readable-type')) {
    window.RUBYST_SET_CHAT_BOOT_VIEWPORT?.(true);
  }
  rbConnectSupport(requestId);
};

const rbRetrySupport = () => {
  if (!rbSupportRoot.classList.contains('support-chat-is-open')) {
    rbOpenSupport();
    return;
  }
  const requestId = ++rbSupportOpenRequest;
  rbSupportIsOpening = true;
  rbSupportRoot.classList.remove('support-chat-is-ready', 'support-chat-load-failed', 'support-chat-has-native-shell');
  rbConnectSupport(requestId);
};

window.RUBYST_OPEN_SUPPORT = rbOpenSupport;
window.RUBYST_CLOSE_SUPPORT = rbCloseSupport;

document.addEventListener('click', (event) => {
  const trigger = event.target.closest?.('[data-support-trigger],[data-phone-support],[data-rb-product-support],.phone-control-support');
  const retry = event.target.closest?.('[data-support-retry]');
  const close = event.target.closest?.('.support-chat-drawer-close,[data-support-backdrop]');
  if (retry) {
    event.preventDefault();
    rbRetrySupport();
    return;
  }
  if (trigger) {
    event.preventDefault();
    if (rbSupportRoot.classList.contains('support-chat-is-open')) rbCloseSupport();
    else rbOpenSupport(trigger);
    return;
  }
  if (close) {
    event.preventDefault();
    rbCloseSupport();
    return;
  }
});

document.addEventListener('keydown', (event) => {
  if (event.key === 'Escape' && rbSupportRoot.classList.contains('support-chat-is-open')) rbCloseSupport();
});

/* A phone page can enter Safari's back-forward cache while the asynchronous
   close sequence is waiting for the keyboard or a stable paint. Finish the
   teardown synchronously before that snapshot is stored. Otherwise the next
   visit can inherit Inbox's narrow viewport without the chat being visible,
   which makes the entire 1280px storefront appear hugely zoomed and cropped. */
const rbResetSupportForPageLifecycle = ({ closeRuntime = true } = {}) => {
  if (!rbSupportRoot.classList.contains('phone-readable-type')) return;
  rbSupportOpenRequest += 1;
  rbCancelShopifyChatOpening();
  rbSupportIsOpening = false;
  rbSupportIsClosing = false;
  rbSupportQueuedOpen = null;
  window.clearTimeout(rbSupportViewportTimer);
  rbSupportViewportTimer = 0;
  const runtime = rbShopifyChatRuntime();
  if (closeRuntime && runtime?.hasAttribute('open')) {
    if (typeof runtime.close === 'function') runtime.close();
    else runtime.removeAttribute('open');
  }
  rbSetSupportState(false);
  rbSupportRoot.classList.remove(
    'support-chat-is-closing',
    'rb-chat-closing-viewport',
    'rb-chat-viewport-ready',
    'rb-chat-keyboard-open'
  );
  rbResetChatViewportGeometry();
  window.RUBYST_RESTORE_PHONE_VIEWPORT?.();
  window.RUBYST_FLOATING_CART?.reset?.();
};

const rbReconcileSupportPageLifecycle = (forceReset = false) => {
  if (!rbSupportRoot.classList.contains('phone-readable-type')) return;
  const runtimeOpen = rbShopifyChatRuntime()?.hasAttribute('open') || false;
  const themeOpen = rbSupportRoot.classList.contains('support-chat-is-open');
  const nativeViewport = rbSupportRoot.classList.contains('rb-chat-native-viewport');
  const transitionActive = rbSupportIsOpening || rbSupportIsClosing;
  if (forceReset || nativeViewport && !transitionActive && (!themeOpen || !runtimeOpen)) {
    rbResetSupportForPageLifecycle();
    return;
  }
  if (!themeOpen && !rbSupportIsClosing) {
    window.RUBYST_RESTORE_PHONE_VIEWPORT?.();
  }
};

window.addEventListener('pagehide', () => {
  rbResetSupportForPageLifecycle();
  rbSupportRuntimeObserver?.disconnect();
});
window.addEventListener('pageshow', (event) => {
  const navigation = performance.getEntriesByType?.('navigation')?.[0];
  const restored = event.persisted || navigation?.type === 'back_forward';
  rbReconcileSupportPageLifecycle(restored);
  if (restored) {
    window.setTimeout(() => rbReconcileSupportPageLifecycle(true), 80);
    window.setTimeout(() => rbReconcileSupportPageLifecycle(true), 240);
  }
});
document.addEventListener('visibilitychange', () => {
  if (!document.hidden) rbReconcileSupportPageLifecycle(false);
});
window.addEventListener('focus', () => rbReconcileSupportPageLifecycle(false));
window.addEventListener('resize', () => rbFitShopifyChatToPhone(), { passive: true });
window.visualViewport?.addEventListener('resize', () => rbFitShopifyChatToPhone(), { passive: true });
window.visualViewport?.addEventListener('scroll', () => rbFitShopifyChatToPhone(), { passive: true });

/* One shared quick-add response for the home cards and product dock. The
   button itself stays visually unchanged: confirmation is presented in a
   centred status toast while the real product image flies to the header cart. */
let rbCartAddToastTimer = 0;
const rbCartAddMessage = () => {
  const language = document.querySelector('[data-language-select]')?.value
    || document.documentElement.lang
    || 'en-US';
  if (language === 'zh-Hans' || language.toLowerCase().startsWith('zh-cn')) return '成功加入购物车';
  if (language === 'zh-Hant' || language.toLowerCase().startsWith('zh-tw')) return '成功加入購物車';
  if (language.toLowerCase().startsWith('es')) return 'Añadido al carrito';
  if (language.toLowerCase().startsWith('fr')) return 'Ajouté au panier';
  if (language.toLowerCase().startsWith('ar')) return 'تمت الإضافة إلى السلة';
  return 'Successfully added to cart';
};

const rbShowCartAddToast = (message = '', isError = false) => {
  let toast = document.querySelector('[data-rb-cart-add-toast]');
  if (!toast) {
    toast = document.createElement('div');
    toast.className = 'rb-cart-add-toast';
    toast.dataset.rbCartAddToast = '';
    toast.setAttribute('role', isError ? 'alert' : 'status');
    toast.setAttribute('aria-live', 'polite');
    toast.innerHTML = '<span class="rb-cart-add-toast__icon" aria-hidden="true">✓</span><strong></strong>';
    document.body.append(toast);
  }
  toast.setAttribute('role', isError ? 'alert' : 'status');
  toast.classList.toggle('is-error', isError);
  toast.querySelector('strong').textContent = message || rbCartAddMessage();
  window.clearTimeout(rbCartAddToastTimer);
  toast.classList.remove('is-visible');
  void toast.offsetWidth;
  toast.classList.add('is-visible');
  rbCartAddToastTimer = window.setTimeout(() => toast.classList.remove('is-visible'), isError ? 2600 : 1800);
};

const rbCartAddTarget = (sourceElement = null) => {
  const candidates = [
    document.querySelector('[data-rb-floating-cart]:not([aria-hidden="true"]) [data-rb-cart-open]'),
    document.querySelector('.site-header .header-paypal-cart [data-rb-cart-open]'),
    document.querySelector('.article-nav [data-rb-cart-open]'),
    document.querySelector('.phone-control-cart [data-rb-cart-open]'),
    ...document.querySelectorAll('[data-rb-cart-open]'),
  ].filter((element, index, list) => element && list.indexOf(element) === index);
  const viewportWidth = document.documentElement.clientWidth || window.innerWidth;
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
  const visible = candidates.filter((element) => {
    const rect = element.getBoundingClientRect();
    const style = getComputedStyle(element);
    return rect.width > 1
      && rect.height > 1
      && rect.right > 0
      && rect.bottom > 0
      && rect.left < viewportWidth
      && rect.top < viewportHeight
      && style.display !== 'none'
      && style.visibility !== 'hidden'
      && Number.parseFloat(style.opacity || '1') > .02;
  });
  if (!visible.length) return candidates[0] || null;
  if (!sourceElement || visible.length === 1) return visible[0];

  const sourceRect = sourceElement.getBoundingClientRect();
  const sourceX = sourceRect.left + sourceRect.width / 2;
  const sourceY = sourceRect.top + sourceRect.height / 2;
  return visible.reduce((closest, candidate) => {
    const distance = (element) => {
      const rect = element.getBoundingClientRect();
      return Math.hypot(rect.left + rect.width / 2 - sourceX, rect.top + rect.height / 2 - sourceY);
    };
    return distance(candidate) < distance(closest) ? candidate : closest;
  });
};

const rbAnimateProductToCart = (source, imageSource = null) => {
  const sourceElement = rbMotionSourceElement(source);
  const target = rbCartAddTarget(sourceElement);
  if (!sourceElement || !target) return null;
  const startRect = sourceElement.getBoundingClientRect();
  const targetRect = target.getBoundingClientRect();
  const startX = startRect.left + startRect.width / 2;
  const startY = startRect.top + startRect.height / 2;
  const endX = targetRect.left + targetRect.width / 2;
  const endY = targetRect.top + targetRect.height / 2;
  const flyer = document.createElement('span');
  flyer.className = 'mobile-cart-fly rb-cart-add-fly';
  flyer.setAttribute('aria-hidden', 'true');

  const sourceImage = imageSource instanceof HTMLImageElement
    ? imageSource
    : sourceElement.closest('.product-card,[data-rb-desktop-product],.mobile-product-card')?.querySelector('img');
  const sourceUrl = typeof imageSource === 'string'
    ? imageSource
    : sourceImage?.currentSrc || sourceImage?.src || '';
  if (sourceUrl) {
    const image = document.createElement('img');
    image.src = sourceUrl;
    image.alt = '';
    flyer.append(image);
  } else {
    flyer.innerHTML = '<svg viewBox="0 0 48 48"><path d="M7 9h6l4 22h20l4-15H16" fill="none" stroke="currentColor" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  }
  flyer.style.left = `${startX}px`;
  flyer.style.top = `${startY}px`;
  document.body.append(flyer);

  const deltaX = endX - startX;
  const deltaY = endY - startY;
  const arcY = Math.min(-88, deltaY * .16);
  const finish = () => {
    flyer.remove();
    if (typeof target.animate === 'function') {
      target.animate([
        { transform: 'scale(1)' },
        { transform: 'scale(1.16)', offset: .46 },
        { transform: 'scale(1)' },
      ], { duration: 300, easing: 'cubic-bezier(.22,1,.36,1)' });
    }
  };
  if (typeof flyer.animate !== 'function' || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    finish();
    return null;
  }
  const flight = flyer.animate([
    { transform: 'translate3d(-50%,-50%,0) scale(.72)', opacity: .35 },
    { transform: 'translate3d(-50%,-56%,0) scale(1)', opacity: 1, offset: .14 },
    { transform: `translate3d(calc(-50% + ${deltaX * .56}px),calc(-50% + ${deltaY * .56 + arcY}px),0) scale(.64)`, opacity: .96, offset: .58 },
    { transform: `translate3d(calc(-50% + ${deltaX}px),calc(-50% + ${deltaY}px),0) scale(.12)`, opacity: .08 },
  ], { duration: 590, easing: 'cubic-bezier(.22,1,.36,1)', fill: 'forwards' });
  flight.finished.then(finish).catch(finish);
  return flyer;
};

window.RUBYST_SHOW_CART_NOTICE = rbShowCartAddToast;
window.RUBYST_ANIMATE_TO_CART = rbAnimateProductToCart;

/* Subscriptions can be selected and checked out as a guest. Keep the public
   hook for older product scripts, but never interrupt the purchase flow with
   an account drawer. Shopify links the completed order to the checkout
   contact details. */
const rbRequireSubscriptionLogin = () => false;
window.RUBYST_REQUIRE_SUBSCRIPTION_LOGIN = rbRequireSubscriptionLogin;

const setupRubystPurchaseOptions = () => {
  document.querySelectorAll('[data-rb-purchase-options]').forEach((options) => {
    options.addEventListener('click', (event) => {
      const choice = event.target.closest('[data-rb-purchase-option]');
      if (!choice || !options.contains(choice)) return;
      event.preventDefault();
      const input = options.querySelector('[data-rb-selling-plan-input]');
      const sellingPlan = choice.dataset.rbSellingPlan || '';
      if (sellingPlan && rbRequireSubscriptionLogin(choice)) return;
      options.querySelectorAll('[data-rb-purchase-option]').forEach((button) => {
        const selected = button === choice;
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-pressed', String(selected));
      });
      if (input) {
        input.value = sellingPlan;
        input.disabled = !sellingPlan;
      }
    });
  });
};

setupRubystPurchaseOptions();

const setupRubystQuantityPricing = () => {
  document.querySelectorAll('[data-rb-quantity-pricing]').forEach((buybox) => {
    if (buybox.dataset.rbQuantityPricingReady === 'true') return;
    const quantitySelect = buybox.querySelector('select[name="quantity"]');
    const selectedTotal = buybox.querySelector('[data-rb-selected-total]');
    const purchaseOptions = buybox.querySelector('[data-rb-purchase-options]');
    if (!quantitySelect || !selectedTotal) return;

    const currency = buybox.dataset.rbCurrency || 'USD';
    const locale = document.documentElement.lang || navigator.language || 'en-US';
    const formatTotal = (cents) => {
      const amount = Number(cents || 0) / 100;
      try {
        return `${new Intl.NumberFormat(locale, {
          style: 'currency',
          currency,
          currencyDisplay: 'narrowSymbol',
        }).format(amount)} ${currency}`;
      } catch (_) {
        return `${amount.toFixed(2)} ${currency}`;
      }
    };

    const updateTotals = () => {
      const quantity = Math.max(1, Number.parseInt(quantitySelect.value, 10) || 1);
      buybox.querySelectorAll('[data-rb-purchase-option]').forEach((option) => {
        const unitPrice = Number(option.dataset.rbOptionUnitPrice);
        const optionTotal = option.querySelector('[data-rb-option-total]');
        if (optionTotal && Number.isFinite(unitPrice)) optionTotal.textContent = formatTotal(unitPrice * quantity);
      });

      const selectedOption = buybox.querySelector('[data-rb-purchase-option].is-selected');
      const selectedUnitPrice = Number(selectedOption?.dataset.rbOptionUnitPrice ?? buybox.dataset.rbBaseUnitPrice);
      if (Number.isFinite(selectedUnitPrice)) selectedTotal.textContent = formatTotal(selectedUnitPrice * quantity);
      selectedTotal.setAttribute('aria-label', `Total for ${quantity} item${quantity === 1 ? '' : 's'}`);
    };

    quantitySelect.addEventListener('input', updateTotals);
    quantitySelect.addEventListener('change', updateTotals);
    purchaseOptions?.addEventListener('click', () => window.requestAnimationFrame(updateTotals));
    buybox.dataset.rbQuantityPricingReady = 'true';
    updateTotals();
  });
};

setupRubystQuantityPricing();

const setupRubystCommunityReviews = () => {
  document.querySelectorAll('[data-rb-community-reviews]').forEach((root) => {
    const productId = root.dataset.rbCommunityReviews;
    const list = root.querySelector('[data-rb-community-review-list]');
    const toggle = root.querySelector('[data-rb-community-review-toggle]');
    const form = root.querySelector('[data-rb-community-review-form]');
    const note = root.querySelector('[data-rb-community-review-note]');
    const photoInput = form?.querySelector('[name="photoFile"]');
    const photoFileName = form?.querySelector('[data-review-file-name]');
    const reviewDateField = form?.querySelector('[data-review-date]');
    if (!productId || !list || !toggle || !form) return;

    const reviewLocale = document.documentElement.lang || navigator.languages?.[0] || navigator.language || 'en-US';
    const formatReviewDate = (value = Date.now()) => {
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return '';
      try { return new Intl.DateTimeFormat(reviewLocale, { year: 'numeric', month: 'long', day: 'numeric' }).format(date); }
      catch (_) { return new Intl.DateTimeFormat('en-US', { year: 'numeric', month: 'long', day: 'numeric' }).format(date); }
    };
    const language = String(reviewLocale).toLowerCase();
    const reviewedOn = language.startsWith('zh') ? '评论于' : language.startsWith('es') ? 'Reseñado el' : language.startsWith('fr') ? 'Avis publié le' : language.startsWith('ar') ? 'تمت المراجعة في' : 'Reviewed on';
    const verifiedPurchase = language.startsWith('zh') ? '已验证购买' : language.startsWith('es') ? 'Compra verificada' : language.startsWith('fr') ? 'Achat vérifié' : language.startsWith('ar') ? 'عملية شراء موثقة' : 'Verified Purchase';
    const syncReviewDate = () => { if (reviewDateField) reviewDateField.value = formatReviewDate(); };
    syncReviewDate();
    root.closest('.rb-shell')?.querySelectorAll('[data-rb-review-date]').forEach((node) => {
      const value = node.dataset.rbReviewDate;
      const formatted = value ? formatReviewDate(`${value}T12:00:00`) : '';
      if (formatted) node.textContent = `${reviewedOn} ${formatted}`;
    });
    root.closest('.rb-shell')?.querySelectorAll('[data-rb-verified-purchase]').forEach((node) => { node.textContent = verifiedPurchase; });

    photoInput?.addEventListener('change', () => {
      const files = Array.from(photoInput.files || []);
      photoInput.setCustomValidity(files.length > 5 ? 'You can upload up to 5 photos.' : '');
      if (photoFileName) photoFileName.textContent = files.length ? files.map((file) => file.name).join(', ') : 'No photos selected';
    });

    const existingBodies = new Set(Array.from(root.closest('.rb-shell')?.querySelectorAll('.rb-review-grid blockquote p') || [])
      .map((node) => node.textContent.trim().toLowerCase()).filter(Boolean));
    let reviews = [];

    const render = () => {
      list.replaceChildren();
      const seen = new Set(existingBodies);
      reviews.forEach((review) => {
        const bodyKey = String(review.body || '').trim().toLowerCase();
        if (!bodyKey || seen.has(bodyKey)) return;
        seen.add(bodyKey);
        const card = document.createElement('blockquote');
        card.className = 'rb-community-review';
        const identity = document.createElement('div');
        identity.className = 'rb-community-review__identity';
        const avatar = document.createElement('span');
        avatar.className = 'rb-community-review__avatar';
        avatar.setAttribute('aria-hidden', 'true');
        avatar.textContent = String(review.name || 'R').trim().charAt(0).toUpperCase() || 'R';
        const customerName = document.createElement('strong');
        customerName.textContent = review.name || 'RUBYST customer';
        identity.append(avatar, customerName);
        const ratingRow = document.createElement('div');
        ratingRow.className = 'rb-community-review__rating';
        const stars = document.createElement('span');
        const purchase = document.createElement('span');
        const body = document.createElement('p');
        const reviewTime = document.createElement('time');
        const rating = Math.max(1, Math.min(5, Number(review.rating) || 5));
        stars.textContent = `${'★'.repeat(rating)}${'☆'.repeat(5 - rating)}`;
        stars.setAttribute('aria-label', `${rating} out of 5 stars`);
        purchase.textContent = verifiedPurchase;
        ratingRow.append(stars, purchase);
        const createdAt = review.createdAt || review.created_at;
        const formattedDate = createdAt ? formatReviewDate(createdAt) : '';
        if (formattedDate) {
          reviewTime.dateTime = new Date(createdAt).toISOString();
          reviewTime.textContent = `${reviewedOn} ${formattedDate}`;
        } else {
          reviewTime.hidden = true;
        }
        body.textContent = review.body || '';
        card.append(identity, ratingRow, reviewTime, body);
        const photos = window.RubystData?.normalizeReviewPhotoUrls
          ? window.RubystData.normalizeReviewPhotoUrls(review.photos, review.photo_urls, review.photo)
          : [review.photo].filter(Boolean);
        if (photos.length) {
          const gallery = document.createElement('div');
          gallery.className = 'rb-community-review-photos';
          photos.forEach((url, index) => {
            const image = document.createElement('img');
            image.src = url;
            image.alt = `Customer review photo ${index + 1} of ${photos.length}`;
            image.loading = 'lazy';
            gallery.append(image);
          });
          card.append(gallery);
        }
        list.append(card);
      });
      const allRatings = [...reviews, ...(Array.isArray(window.RUBYST_MANAGED_PRODUCT_REVIEWS) ? window.RUBYST_MANAGED_PRODUCT_REVIEWS : [])];
      if (allRatings.length) {
        const average = allRatings.reduce((sum, review) => sum + Math.max(1, Math.min(5, Number(review.rating) || 5)), 0) / allRatings.length;
        document.querySelectorAll('[data-rb-desktop-rating]').forEach((node) => { node.textContent = average.toFixed(1); });
        document.querySelectorAll('[data-rb-desktop-review-count]').forEach((node) => { node.textContent = `${allRatings.length} approved ${allRatings.length === 1 ? 'review' : 'reviews'}`; });
      }
    };

    const refresh = async () => {
      if (!window.RubystData?.configured()) return;
      try {
        reviews = await window.RubystData.getApprovedReviews(productId);
        render();
      } catch (_) {
        if (note) note.textContent = 'Reviews are temporarily unavailable.';
      }
    };

    toggle.addEventListener('click', () => {
      form.hidden = !form.hidden;
      toggle.setAttribute('aria-expanded', String(!form.hidden));
      if (!form.hidden) {
        syncReviewDate();
        form.querySelector('[name="name"]')?.focus({ preventScroll: true });
      }
    });

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      const data = new FormData(form);
      const submit = form.querySelector('button[type="submit"]');
      if (!window.RubystData?.configured()) {
        if (note) note.textContent = 'Review service is still loading. Please try again.';
        return;
      }
      submit.disabled = true;
      if (note) note.textContent = 'Submitting your review…';
      try {
        await window.RubystData.submitReview({
          productId,
          name: data.get('name'),
          rating: data.get('rating'),
          title: new Date().toISOString().slice(0, 10),
          body: data.get('body'),
          photoFiles: data.getAll('photoFile'),
        });
        form.reset();
        syncReviewDate();
        if (photoFileName) photoFileName.textContent = 'No photos selected';
        form.hidden = true;
        toggle.setAttribute('aria-expanded', 'false');
        await refresh();
        if (note) note.textContent = 'Thank you. Your review is now live.';
      } catch (error) {
        if (note) note.textContent = error.message || 'We could not submit your review. Please try again.';
      } finally {
        submit.disabled = false;
      }
    });

    refresh();
  });
};

setupRubystCommunityReviews();

const setupRubystDesktopProduct = () => {
  const product = document.querySelector('[data-rb-desktop-product]');
  if (!product) return;

  const mainImage = product.querySelector('[data-rb-desktop-main-image]');
  const zoomLayer = document.querySelector('[data-rb-desktop-zoom-layer]');
  const zoomImage = zoomLayer?.querySelector('[data-rb-desktop-zoom-image]');
  const recordsLayer = document.querySelector('[data-rb-desktop-records-layer]');
  const recordsTriggers = Array.from(product.querySelectorAll('[data-rb-desktop-records-open]'));
  let activeRecordsTrigger = null;
  let recordsCloseTimer = 0;
  let zoomCloseTimer = 0;
  let activeZoomTrigger = null;
  const desktopVideoSection = product.querySelector('[data-rb-desktop-video]');
  const desktopVideoPanels = desktopVideoSection
    ? Array.from(desktopVideoSection.querySelectorAll('[data-rb-desktop-video-panel]'))
    : [];
  const desktopVideoSelectors = desktopVideoSection
    ? Array.from(desktopVideoSection.querySelectorAll('[data-rb-desktop-video-select]'))
    : [];

  desktopVideoPanels.forEach((panel) => {
    panel.querySelectorAll('video').forEach((video) => {
      video.defaultMuted = true;
      video.muted = true;
      video.loop = true;
      video.playsInline = true;
    });
  });

  const pauseDesktopVideos = () => {
    desktopVideoPanels.forEach((panel) => {
      panel.querySelectorAll('video').forEach((video) => video.pause());
    });
  };

  const getDesktopVideoPanel = (selector) => {
    const target = selector?.dataset.videoTarget
      || selector?.dataset.rbDesktopVideoSelect
      || selector?.getAttribute('aria-controls');
    if (target) {
      const match = desktopVideoPanels.find((panel) => (
        panel.dataset.rbDesktopVideoPanel === target || panel.id === target
      ));
      if (match) return match;
    }
    const index = desktopVideoSelectors.indexOf(selector);
    return desktopVideoPanels[index] || desktopVideoPanels[0] || null;
  };

  const selectDesktopVideo = (selector) => {
    const activePanel = getDesktopVideoPanel(selector);
    if (!activePanel) return null;
    pauseDesktopVideos();
    desktopVideoPanels.forEach((panel) => {
      const selected = panel === activePanel;
      panel.classList.toggle('is-active', selected);
      panel.hidden = !selected;
      panel.setAttribute('aria-hidden', String(!selected));
    });
    desktopVideoSelectors.forEach((button) => {
      const selected = button === selector;
      button.classList.toggle('is-active', selected);
      button.setAttribute('aria-pressed', String(selected));
    });
    return activePanel;
  };

  if (desktopVideoPanels.length) {
    const initialPanel = desktopVideoSection.querySelector('[data-rb-desktop-video-panel].is-active') || desktopVideoPanels[0];
    const initialSelector = desktopVideoSelectors.find((button) => getDesktopVideoPanel(button) === initialPanel)
      || desktopVideoSelectors[0];
    if (initialSelector) selectDesktopVideo(initialSelector);
    else {
      desktopVideoPanels.forEach((panel) => {
        const selected = panel === initialPanel;
        panel.classList.toggle('is-active', selected);
        panel.hidden = !selected;
        panel.setAttribute('aria-hidden', String(!selected));
      });
    }

    const desktopVideoPlaybackObserver = new IntersectionObserver(([entry]) => {
      const activeVideo = desktopVideoSection.querySelector('[data-rb-desktop-video-panel].is-active video');
      if (!activeVideo) return;
      if (entry?.isIntersecting && entry.intersectionRatio >= .3) {
        activeVideo.play().catch(() => {});
      } else if (!activeVideo.paused) {
        activeVideo.pause();
      }
    }, { threshold: [0, .3] });
    desktopVideoPlaybackObserver.observe(desktopVideoSection);
    window.addEventListener('pagehide', () => desktopVideoPlaybackObserver.disconnect(), { once: true });
  }

  const openRecords = (trigger = recordsTriggers[0]) => {
    if (!recordsLayer || !trigger) return;
    window.clearTimeout(recordsCloseTimer);
    recordsLayer.classList.remove('is-closing');
    activeRecordsTrigger = trigger;
    recordsLayer.hidden = false;
    rbSetMorphTarget(recordsLayer, recordsLayer.querySelector('.rb-desktop-records__dialog'), trigger);
    void recordsLayer.offsetWidth;
    recordsTriggers.forEach((button) => button.setAttribute('aria-expanded', 'true'));
    document.documentElement.classList.add('rb-desktop-records-open');
    recordsLayer.querySelector('[data-rb-desktop-records-close]:not(.rb-desktop-records__backdrop)')?.focus();
  };

  const closeRecords = (source = null) => {
    if (!recordsLayer || recordsLayer.hidden) return;
    window.clearTimeout(recordsCloseTimer);
    rbSetMorphTarget(recordsLayer, recordsLayer.querySelector('.rb-desktop-records__dialog'), source, activeRecordsTrigger);
    recordsLayer.classList.add('is-closing');
    recordsTriggers.forEach((button) => button.setAttribute('aria-expanded', 'false'));
    document.documentElement.classList.remove('rb-desktop-records-open');
    const focusTarget = activeRecordsTrigger;
    activeRecordsTrigger = null;
    recordsCloseTimer = window.setTimeout(() => {
      recordsLayer.hidden = true;
      recordsLayer.classList.remove('is-closing');
      focusTarget?.focus();
    }, 520);
  };

  product.addEventListener('click', (event) => {
    const videoSelector = event.target.closest('[data-rb-desktop-video-select]');
    if (videoSelector && desktopVideoSection?.contains(videoSelector)) {
      event.preventDefault();
      const activePanel = selectDesktopVideo(videoSelector);
      const activeVideo = activePanel?.querySelector('video');
      if (activeVideo) {
        activeVideo.play().catch(() => {});
      }
    }

    const thumb = event.target.closest('[data-rb-desktop-thumb]');
    if (thumb && mainImage) {
      event.preventDefault();
      const nextImage = thumb.dataset.image;
      if (!nextImage || nextImage === mainImage.src) return;
      product.querySelectorAll('[data-rb-desktop-thumb]').forEach((button) => {
        const selected = button === thumb;
        button.classList.toggle('is-active', selected);
        button.setAttribute('aria-pressed', String(selected));
      });
      mainImage.classList.add('is-changing');
      window.setTimeout(() => {
        mainImage.src = nextImage;
        mainImage.alt = thumb.dataset.alt || mainImage.alt;
        mainImage.classList.remove('is-changing');
      }, 120);
    }

    if (event.target.closest('[data-rb-desktop-video-jump]')) {
      event.preventDefault();
      desktopVideoSection?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      window.setTimeout(() => {
        const activePanel = desktopVideoSection?.querySelector('[data-rb-desktop-video-panel].is-active')
          || desktopVideoPanels[0];
        activePanel?.querySelector('video')?.play().catch(() => {});
      }, 450);
    }

    if (event.target.closest('[data-rb-desktop-zoom]') && mainImage && zoomLayer && zoomImage) {
      window.clearTimeout(zoomCloseTimer);
      zoomLayer.classList.remove('is-closing');
      activeZoomTrigger = event.target.closest('[data-rb-desktop-zoom]');
      zoomImage.src = mainImage.currentSrc || mainImage.src;
      zoomImage.alt = mainImage.alt;
      zoomLayer.hidden = false;
      rbSetMorphTarget(zoomLayer, zoomImage, activeZoomTrigger);
      void zoomLayer.offsetWidth;
      document.documentElement.classList.add('rb-desktop-zoom-open');
    }

    const recordsOpen = event.target.closest('[data-rb-desktop-records-open]');
    if (recordsOpen) {
      event.preventDefault();
      openRecords(recordsOpen);
    }
  });

  const closeZoom = (source = null) => {
    if (!zoomLayer || zoomLayer.hidden) return;
    window.clearTimeout(zoomCloseTimer);
    rbSetMorphTarget(zoomLayer, zoomImage, source, activeZoomTrigger);
    zoomLayer.classList.add('is-closing');
    document.documentElement.classList.remove('rb-desktop-zoom-open');
    zoomCloseTimer = window.setTimeout(() => {
      zoomLayer.hidden = true;
      zoomLayer.classList.remove('is-closing');
      activeZoomTrigger = null;
    }, 520);
  };
  zoomLayer?.addEventListener('click', (event) => {
    if (event.target === zoomLayer || event.target.closest('[data-rb-desktop-zoom-close]')) closeZoom(event);
  });
  recordsLayer?.addEventListener('click', (event) => {
    if (event.target.closest('[data-rb-desktop-records-close]')) closeRecords(event);
  });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && zoomLayer && !zoomLayer.hidden) closeZoom(activeZoomTrigger);
    if (event.key === 'Escape' && recordsLayer && !recordsLayer.hidden) closeRecords(activeRecordsTrigger);
  });

  const reviews = Array.isArray(window.RUBYST_PRODUCT_REVIEWS) ? window.RUBYST_PRODUCT_REVIEWS : [];
  const rating = product.querySelector('[data-rb-desktop-rating]');
  const count = product.querySelector('[data-rb-desktop-review-count]');
  if (reviews.length) {
    const average = reviews.reduce((total, review) => total + Math.max(1, Math.min(5, Number(review.rating) || 5)), 0) / reviews.length;
    if (rating) rating.textContent = average.toFixed(1);
    if (count) count.textContent = `${reviews.length} approved ${reviews.length === 1 ? 'review' : 'reviews'}`;
  } else {
    if (rating) rating.textContent = 'New';
    if (count) count.textContent = 'Be the first to review';
  }
};

setupRubystDesktopProduct();

let rubystSupportCloseTimer = 0;
let rubystSupportSource = null;
document.addEventListener('click', (event) => {
  const open = event.target.closest('[data-rb-support-open]');
  const close = event.target.closest('[data-rb-support-close]');
  const layer = document.querySelector('[data-rb-support-layer]');
  if (open && layer) {
    window.clearTimeout(rubystSupportCloseTimer);
    layer.classList.remove('is-closing');
    rubystSupportSource = open;
    layer.hidden = false;
    rbSetMorphTarget(layer, layer.querySelector('.rb-support-sheet'), open);
    void layer.offsetWidth;
    document.documentElement.classList.add('rb-support-open');
  }
  if (close && layer) {
    window.clearTimeout(rubystSupportCloseTimer);
    rbSetMorphTarget(layer, layer.querySelector('.rb-support-sheet'), event, rubystSupportSource);
    layer.classList.add('is-closing');
    document.documentElement.classList.remove('rb-support-open');
    rubystSupportCloseTimer = window.setTimeout(() => {
      layer.hidden = true;
      layer.classList.remove('is-closing');
    }, 520);
  }
});

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;
  const layer = document.querySelector('[data-rb-support-layer]');
  if (!layer || layer.hidden) return;
  window.clearTimeout(rubystSupportCloseTimer);
  rbSetMorphTarget(layer, layer.querySelector('.rb-support-sheet'), rubystSupportSource);
  layer.classList.add('is-closing');
  document.documentElement.classList.remove('rb-support-open');
  rubystSupportCloseTimer = window.setTimeout(() => {
    layer.hidden = true;
    layer.classList.remove('is-closing');
  }, 520);
});

document.querySelectorAll('[data-campaign]').forEach((campaign) => {
  const start = campaign.dataset.start ? new Date(campaign.dataset.start).getTime() : null;
  const end = campaign.dataset.end ? new Date(campaign.dataset.end).getTime() : null;
  const countdown = campaign.querySelector('[data-campaign-countdown]');

  const updateCampaign = () => {
    const now = Date.now();
    const validStart = !start || Number.isNaN(start) || now >= start;
    const validEnd = !end || Number.isNaN(end) || now < end;
    campaign.hidden = !(validStart && validEnd);
    if (!countdown || !end || Number.isNaN(end) || !validEnd) return;

    const remaining = Math.max(0, end - now);
    const days = Math.floor(remaining / 86400000);
    const hours = Math.floor((remaining % 86400000) / 3600000);
    const minutes = Math.floor((remaining % 3600000) / 60000);
    const seconds = Math.floor((remaining % 60000) / 1000);
    countdown.textContent = `${days ? `${days}d ` : ''}${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  };

  updateCampaign();
  const hasSchedule = (start && !Number.isNaN(start)) || (end && !Number.isNaN(end));
  if (hasSchedule) {
    const campaignTimer = window.setInterval(() => {
      if (!document.hidden) updateCampaign();
    }, 1000);
    window.addEventListener('pagehide', () => window.clearInterval(campaignTimer), { once: true });
  }
});

const rubystCartDrawer = document.querySelector('[data-rb-cart-drawer]');

if (rubystCartDrawer) {
  window.RUBYST_CART_CONTROLLER_READY = true;
  const cartSheet = rubystCartDrawer.querySelector('.rb-cart-drawer__sheet');
  const cartItems = rubystCartDrawer.querySelector('[data-rb-cart-items]');
  const cartFooter = rubystCartDrawer.querySelector('[data-rb-cart-footer]');
  const cartTotal = rubystCartDrawer.querySelector('[data-rb-cart-total]');
  const cartDrawerCount = rubystCartDrawer.querySelector('[data-rb-cart-drawer-count]');
  const cartNotice = rubystCartDrawer.querySelector('[data-rb-cart-notice]');
  const selectAll = rubystCartDrawer.querySelector('[data-rb-cart-select-all]');
  const selectedCount = rubystCartDrawer.querySelector('[data-rb-cart-selected-count]');
  const checkoutForm = rubystCartDrawer.querySelector('[data-rb-cart-checkout-form]');
  const checkoutButton = rubystCartDrawer.querySelector('[data-rb-cart-checkout]');
  const expandButton = rubystCartDrawer.querySelector('[data-rb-cart-expand]');
  const cartPurchaseOptions = rubystCartDrawer.querySelector('[data-rb-cart-purchase-options]');
  const initialCartData = rubystCartDrawer.querySelector('[data-rb-initial-cart]');
  const cartUrl = rubystCartDrawer.dataset.cartUrl || '/cart';
  const jsonRoute = (route) => {
    const value = String(route || '').trim();
    if (!value) return '/cart.js';
    const match = value.match(/^([^?#]*)([?#].*)?$/);
    const pathname = match?.[1] || value;
    const suffix = match?.[2] || '';
    return `${pathname.endsWith('.js') ? pathname : `${pathname}.js`}${suffix}`;
  };
  const addUrl = jsonRoute(rubystCartDrawer.dataset.cartAddUrl || '/cart/add');
  const changeUrl = jsonRoute(rubystCartDrawer.dataset.cartChangeUrl || '/cart/change');
  const updateUrl = jsonRoute(rubystCartDrawer.dataset.cartUpdateUrl || '/cart/update');
  const cartJsonUrl = jsonRoute(cartUrl);
  const absoluteCartRoute = (route) => {
    const value = String(route || '');
    if (/^https?:\/\//i.test(value)) return value;
    return `${window.location.origin}${value.startsWith('/') ? '' : '/'}${value}`;
  };
  const xhrCartJson = (route, options = {}) => new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    const signal = options.signal;
    if (signal?.aborted) {
      reject(new DOMException('The operation was aborted.', 'AbortError'));
      return;
    }
    try {
      request.open(options.method || 'GET', absoluteCartRoute(route), true);
    } catch (_) {
      reject(new Error('The cart connection could not be opened.'));
      return;
    }
    request.withCredentials = true;
    Object.entries(options.headers || {}).forEach(([name, value]) => {
      try { request.setRequestHeader(name, value); } catch (_) {}
    });
    request.addEventListener('load', () => {
      let data;
      try { data = JSON.parse(request.responseText || '{}'); } catch (_) {
        reject(new Error('The cart returned an unreadable response.'));
        return;
      }
      if (request.status < 200 || request.status >= 300) {
        reject(new Error(data.description || data.message || 'The cart request was not accepted.'));
        return;
      }
      resolve(data);
    }, { once: true });
    request.addEventListener('error', () => reject(new Error('The cart connection was interrupted.')), { once: true });
    request.addEventListener('timeout', () => reject(new Error('The cart request timed out.')), { once: true });
    request.addEventListener('abort', () => reject(new DOMException('The operation was aborted.', 'AbortError')), { once: true });
    signal?.addEventListener('abort', () => request.abort(), { once: true });
    request.timeout = 10000;
    try { request.send(options.body || null); } catch (_) {
      reject(new Error('The cart connection could not be sent.'));
    }
  });
  const requestCartJson = async (route, options = {}) => {
    const requestOptions = { credentials: 'same-origin', ...options };
    try {
      const response = await fetch(absoluteCartRoute(route), requestOptions);
      const text = await response.text();
      let data;
      try { data = JSON.parse(text || '{}'); } catch (_) {
        throw new Error('The cart returned an unreadable response.');
      }
      if (!response.ok) {
        const error = new Error(data.description || data.message || 'The cart request was not accepted.');
        error.code = 'RB_CART_HTTP_ERROR';
        throw error;
      }
      return data;
    } catch (fetchError) {
      if (fetchError?.name === 'AbortError' || fetchError?.code === 'RB_CART_HTTP_ERROR') throw fetchError;
      /* Safari private browsing, VPNs and injected wallet scripts can reject a
         standards-compliant fetch before it reaches Shopify. Retry the same
         idempotent cart read/update through XHR instead of surfacing WebKit's
         low-level “string did not match” DOMException to the shopper. */
      try { return await xhrCartJson(route, options); } catch (_) {
        const error = new Error('Cart request failed.');
        error.code = 'RB_CART_REQUEST_FAILED';
        throw error;
      }
    }
  };
  const selectedKeys = new Set();
  const knownKeys = new Set();
  const swipeStarts = new WeakMap();
  let currentCart = null;
  let allowCheckoutSubmit = false;
  let checkoutAwaitingQuantity = false;
  let cartCloseTimer = 0;
  let cartDrag = null;
  let suppressHandleClickUntil = 0;
  let cartRequest = null;
  let cartFetchedAt = 0;
  let cartMotionSource = null;
  let serverCart = null;
  let cartUpdateRunner = null;
  let cartUpdateRetryTimer = 0;
  let cartUiHydrated = false;
  const pendingSellingPlanUpdates = new Map();
  let sellingPlanRunner = null;
  let sellingPlanDebounceTimer = 0;
  let sellingPlanRevision = 0;
  const quantityRevisions = new Map();
  const pendingQuantityUpdates = new Map();

  const escapeHtml = (value = '') => String(value).replace(/[&<>'"]/g, (character) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;'
  }[character]));

  const formatMoney = (cents, currency = 'USD') => new Intl.NumberFormat(
    document.documentElement.lang || 'en-US',
    { style: 'currency', currency }
  ).format(Number(cents || 0) / 100);

  const moneyTextToCents = (value) => {
    const number = Number(String(value || '').replace(/[^0-9.-]/g, ''));
    return Number.isFinite(number) ? Math.round(number * 100) : null;
  };

  const cartImageUrl = (value, width = 480) => {
    try {
      const url = new URL(String(value || ''), window.location.origin);
      if (url.hostname === 'cdn.shopify.com' || url.hostname.endsWith('.shopifycdn.com')) {
        url.searchParams.set('width', String(width));
      }
      return url.href;
    } catch (_) {
      return String(value || '');
    }
  };

  const sellingPlanCatalog = window.RUBYST_SELLING_PLANS || Object.fromEntries(
    Object.values(window.RUBYST_PRODUCTS || {})
      .filter((product) => product?.variantId && product?.subscription)
      .map((product) => [String(product.variantId), {
        productTitle: product.title,
        oneTimePrice: product.formattedPrice,
        plans: [{
          id: product.subscription.id,
          name: product.subscription.name,
          price: product.subscription.formattedPrice,
        }],
      }])
  );

  const setNotice = (message = '', isError = false) => {
    if (!cartNotice) return;
    cartNotice.textContent = message;
    cartNotice.hidden = !message;
    cartNotice.classList.toggle('is-error', isError);
  };

  const waitForCart = (milliseconds) => new Promise((resolve) => window.setTimeout(resolve, milliseconds));

  /* Automatic Shopify discounts can replace every line-item key after any
     cart mutation. A stable identity lets rapid taps follow the same product
     even when the key returned by Shopify changes between requests. */
  const cartItemIdentity = (item) => {
    const properties = Object.entries(item?.properties || {})
      .map(([name, value]) => [String(name), String(value ?? '')])
      .sort(([left], [right]) => left.localeCompare(right));
    const sellingPlan = item?.selling_plan_allocation?.selling_plan?.id
      || item?.selling_plan_allocation?.selling_plan?.name
      || '';
    const originalUnitPrice = Number(item?.original_price ?? item?.price ?? 0);
    const finalUnitPrice = Number(item?.final_price ?? (
      item?.quantity ? Number(item?.final_line_price || 0) / Number(item.quantity) : originalUnitPrice
    ));
    const priceKind = originalUnitPrice > 0 && finalUnitPrice === 0 ? 'free' : 'paid';
    return JSON.stringify([
      String(item?.variant_id || item?.id || ''),
      String(sellingPlan),
      properties,
      priceKind,
    ]);
  };

  /* A selling-plan change replaces the Shopify line key, but it is still the
     same product row from the shopper's point of view. Keep a second identity
     that deliberately excludes the plan so the final click can follow that
     row across one-time/subscription mutations. */
  const cartPurchaseIdentity = (item) => {
    const properties = Object.entries(item?.properties || {})
      .map(([name, value]) => [String(name), String(value ?? '')])
      .sort(([left], [right]) => left.localeCompare(right));
    const originalUnitPrice = Number(item?.original_price ?? item?.price ?? 0);
    const finalUnitPrice = Number(item?.final_price ?? (
      item?.quantity ? Number(item?.final_line_price || 0) / Number(item.quantity) : originalUnitPrice
    ));
    const priceKind = originalUnitPrice > 0 && finalUnitPrice === 0 ? 'free' : 'paid';
    return JSON.stringify([
      String(item?.variant_id || item?.id || ''),
      properties,
      priceKind,
    ]);
  };

  const findCartPurchaseIdentity = (cart, identity) => {
    const index = cart?.items?.findIndex((item) => cartPurchaseIdentity(item) === identity) ?? -1;
    return index >= 0 ? { item: cart.items[index], index } : null;
  };

  const findCartIdentity = (cart, identity) => {
    const index = cart?.items?.findIndex((item) => cartItemIdentity(item) === identity) ?? -1;
    return index >= 0 ? { item: cart.items[index], index } : null;
  };

  const cartQuantityMatches = (cart, job) => {
    const match = findCartIdentity(cart, job.identity);
    return job.quantity === 0 ? !match : Number(match?.item?.quantity) === job.quantity;
  };

  const fetchServerCart = async () => {
    const cart = await requestCartJson(cartJsonUrl, {
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      cache: 'no-store',
    });
    serverCart = cart;
    return cart;
  };

  const synchronizeCartQuantity = async (job) => {
    let cart = serverCart || currentCart;
    for (let attempt = 0; attempt < 4; attempt += 1) {
      if (cartQuantityMatches(cart, job)) return cart;
      let match = findCartIdentity(cart, job.identity);
      if (!match) {
        cart = await fetchServerCart();
        if (cartQuantityMatches(cart, job)) return cart;
        match = findCartIdentity(cart, job.identity);
        if (!match) throw new Error('Cart line is being refreshed.');
      }

      try {
        /* Use the newest key from the last confirmed Shopify response. If a
           response is lost, repeating an absolute quantity is idempotent. */
        cart = await requestCartJson(changeUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ id: match.item.key, quantity: job.quantity }),
        });
        serverCart = cart;
      } catch (_) {
        /* A mutation may have reached Shopify even when Safari lost its
           response. Re-read first, then resolve the replacement key. */
        cart = await fetchServerCart();
      }

      if (cartQuantityMatches(cart, job)) return cart;
      await waitForCart(70 * (attempt + 1));
    }
    throw new Error('Cart quantity is still synchronizing.');
  };

  const runQuantityUpdateQueue = (immediate = false) => {
    window.clearTimeout(cartUpdateRetryTimer);
    cartUpdateRetryTimer = 0;
    if (cartUpdateRunner) return cartUpdateRunner;

    cartUpdateRunner = (async () => {
      if (!immediate) await waitForCart(48);
      let failures = 0;
      while (pendingQuantityUpdates.size) {
        const [identity, job] = pendingQuantityUpdates.entries().next().value;
        pendingQuantityUpdates.delete(identity);
        try {
          const cart = await synchronizeCartQuantity(job);
          failures = 0;
          /* A newer tap may have arrived while this request was in flight.
             Keep the newer optimistic number painted instead of flashing the
             older server response for one frame. */
          if (!pendingQuantityUpdates.size) {
            cartFetchedAt = performance.now();
            renderCart(cart);
            setNotice('');
          }
        } catch (_) {
          const newerJob = pendingQuantityUpdates.get(identity);
          if (!newerJob || newerJob.revision <= job.revision) pendingQuantityUpdates.set(identity, job);
          failures += 1;
          if (failures >= 3) break;
          await waitForCart(140 * failures);
        }
      }
    })().finally(() => {
      cartUpdateRunner = null;
      if (pendingQuantityUpdates.size) {
        /* Stay silent and retry in the background. The customer keeps the
           instant optimistic quantity instead of seeing a transient error. */
        cartUpdateRetryTimer = window.setTimeout(() => runQuantityUpdateQueue(), 900);
      }
    });

    return cartUpdateRunner;
  };

  const queueCartQuantity = (item, quantity) => {
    const identity = cartItemIdentity(item);
    const revision = (quantityRevisions.get(identity) || 0) + 1;
    quantityRevisions.set(identity, revision);
    pendingQuantityUpdates.set(identity, { identity, quantity, revision });
    runQuantityUpdateQueue();
  };

  const setExpanded = (expanded) => {
    rubystCartDrawer.classList.remove('is-dragging');
    rubystCartDrawer.style.removeProperty('--rb-cart-sheet-height');
    rubystCartDrawer.style.removeProperty('--rb-cart-drag-y');
    rubystCartDrawer.classList.toggle('is-expanded', expanded);
    expandButton?.setAttribute('aria-expanded', String(expanded));
    expandButton?.setAttribute('aria-label', expanded ? 'Collapse cart' : 'Expand cart');
  };

  const useSimplePhoneCartMotion = () => document.documentElement.classList.contains('phone-readable-type')
    || window.matchMedia('(max-width: 749px)').matches;

  const openCart = (source = null) => {
    window.clearTimeout(cartCloseTimer);
    rubystCartDrawer.classList.remove('is-closing');
    cartMotionSource = rbMotionSourceElement(source) || cartMotionSource;
    rubystCartDrawer.hidden = false;
    const simplePhoneMotion = useSimplePhoneCartMotion();
    if (simplePhoneMotion) {
      rubystCartDrawer.style.removeProperty('--rb-motion-x');
      rubystCartDrawer.style.removeProperty('--rb-motion-y');
      rubystCartDrawer.style.removeProperty('--rb-motion-scale');
      rubystCartDrawer.querySelectorAll('img[loading="lazy"]').forEach((image) => { image.loading = 'eager'; });
    } else {
      rbSetMorphTarget(rubystCartDrawer, cartSheet, source, cartMotionSource);
      /* Desktop retains the trigger-to-panel morph. Phones use a much cheaper
         bottom-sheet paint so low-end Android GPUs do not rasterize a scaled,
         blurred full-screen layer before showing the cart. */
      void rubystCartDrawer.offsetWidth;
    }
    document.documentElement.classList.add('rb-cart-open');
    setExpanded(false);
    setNotice('');
    rubystCartDrawer.querySelector('[data-rb-cart-close]')?.focus({ preventScroll: true });
    if (!cartUiHydrated && currentCart) {
      window.requestAnimationFrame(() => {
        if (!cartUiHydrated && currentCart) renderCart(currentCart);
      });
    }
  };

  const closeCart = (source = null) => {
    const simplePhoneMotion = useSimplePhoneCartMotion();
    if (!simplePhoneMotion) rbSetMorphTarget(rubystCartDrawer, cartSheet, source, cartMotionSource);
    rubystCartDrawer.classList.add('is-closing');
    document.documentElement.classList.remove('rb-cart-open');
    setExpanded(false);
    window.clearTimeout(cartCloseTimer);
    cartCloseTimer = window.setTimeout(() => {
      if (!document.documentElement.classList.contains('rb-cart-open')) {
        rubystCartDrawer.hidden = true;
        rubystCartDrawer.classList.remove('is-closing');
      }
    }, simplePhoneMotion ? 280 : 520);
  };

  const cartItemPurchaseOptionsMarkup = (item, index) => {
    const planData = sellingPlanCatalog[String(item.variant_id || item.id)];
    if (!planData?.plans?.length) return '';
    const selectedPlanId = String(item.selling_plan_allocation?.selling_plan?.id || '');
    /* Keep the choice beside the product it changes. Showing one one-time and
       one recurring option prevents the checkout footer from growing into a
       second, duplicated product list on phones. */
    const recurringPlan = planData.plans[0];
    const recurringName = /week/i.test(recurringPlan.name || '')
      ? 'Weekly delivery · Save 5%'
      : /month/i.test(recurringPlan.name || '')
        ? 'Monthly delivery · Save 5%'
        : 'Subscribe & save';
    const choices = [
      { id: '', name: 'One-time purchase · No renewal', price: planData.oneTimePrice },
      { ...recurringPlan, name: recurringName },
    ];
    return `<section class="rb-cart-drawer__item-options rb-cart-drawer__purchase-group" data-rb-cart-purchase-group aria-label="Purchase option for ${escapeHtml(item.product_title || planData.productTitle || '')}">
      <div>${choices.map((choice) => {
        const selected = String(choice.id || '') === selectedPlanId;
        const priceCents = moneyTextToCents(choice.price);
        const displayPrice = String(choice.price || '').replace(/\s+[A-Z]{3}$/i, '');
        return `<button class="${selected ? 'is-selected' : ''}" type="button" data-rb-cart-selling-plan data-line="${index + 1}" data-selling-plan="${escapeHtml(choice.id || '')}"${priceCents === null ? '' : ` data-price-cents="${priceCents}"`} aria-pressed="${selected}"><span>${escapeHtml(choice.name)}</span><strong>${escapeHtml(displayPrice)}</strong></button>`;
      }).join('')}</div>
    </section>`;
  };

  const updateSelection = () => {
    if (!currentCart) return;
    const selectedItems = currentCart.items.filter((item) => selectedKeys.has(item.key));
    const quantity = selectedItems.reduce((total, item) => total + item.quantity, 0);
    const total = selectedItems.reduce((sum, item) => sum + item.final_line_price, 0);
    rubystCartDrawer.querySelectorAll('[data-rb-cart-select]').forEach((checkbox) => {
      checkbox.checked = selectedKeys.has(checkbox.dataset.key);
    });
    if (selectAll) {
      selectAll.checked = currentCart.items.length > 0 && selectedItems.length === currentCart.items.length;
      selectAll.indeterminate = selectedItems.length > 0 && selectedItems.length < currentCart.items.length;
    }
    if (selectedCount) selectedCount.textContent = `${quantity} selected`;
    if (cartTotal) cartTotal.textContent = formatMoney(total, currentCart.currency);
    if (checkoutButton) {
      checkoutButton.disabled = quantity === 0;
      checkoutButton.textContent = quantity ? `Secure checkout · ${quantity}` : 'Select an item to continue';
    }
    /* Purchase options are rendered in each product row. Keep the legacy
       footer mount empty so older cached markup cannot duplicate them below. */
    if (cartPurchaseOptions) {
      cartPurchaseOptions.replaceChildren();
      cartPurchaseOptions.hidden = true;
    }
  };

  const renderCart = (cart) => {
    cartUiHydrated = true;
    const previousItems = currentCart?.items || [];
    const previouslySelectedPurchases = new Set(
      previousItems
        .filter((item) => selectedKeys.has(item.key))
        .map((item) => cartPurchaseIdentity(item))
    );
    const previousPurchases = new Set(previousItems.map((item) => cartPurchaseIdentity(item)));
    currentCart = cart;
    const liveKeys = new Set(cart.items.map((item) => item.key));
    for (const key of [...knownKeys]) {
      if (!liveKeys.has(key)) {
        knownKeys.delete(key);
        selectedKeys.delete(key);
      }
    }
    cart.items.forEach((item) => {
      if (!knownKeys.has(item.key)) {
        const purchaseIdentity = cartPurchaseIdentity(item);
        if (!previousPurchases.has(purchaseIdentity) || previouslySelectedPurchases.has(purchaseIdentity)) {
          selectedKeys.add(item.key);
        }
      }
      knownKeys.add(item.key);
    });
    document.querySelectorAll('.rb-header__cart-count').forEach((count) => { count.textContent = cart.item_count; });
    document.querySelectorAll('[data-rb-cart-open]').forEach((button) => {
      button.setAttribute('aria-label', `Cart with ${cart.item_count} items`);
    });
    if (cartDrawerCount) cartDrawerCount.textContent = `(${cart.item_count})`;
    if (cartFooter) cartFooter.hidden = cart.item_count === 0;
    if (!cartItems) return;

    if (!cart.items.length) {
      cartItems.innerHTML = '<div class="rb-cart-drawer__empty"><strong>Your cart is ready for a ritual.</strong><p>Choose a RUBYST product to get started.</p></div>';
      updateSelection();
      return;
    }

    const previousScrollTop = cartItems.scrollTop;
    const reusableImages = new Map();
    cartItems.querySelectorAll('.rb-cart-drawer__item[data-cart-variant-id]').forEach((row) => {
      const image = row.querySelector('.rb-cart-drawer__media img');
      if (!image) return;
      const variantId = row.dataset.cartVariantId || '';
      const pool = reusableImages.get(variantId) || [];
      pool.push(image);
      reusableImages.set(variantId, pool);
    });
    const activeChoice = document.activeElement?.matches?.('[data-rb-cart-selling-plan]')
      ? {
          variantId: document.activeElement.closest('[data-cart-variant-id]')?.dataset.cartVariantId || '',
          planId: document.activeElement.dataset.sellingPlan || '',
        }
      : null;

    const rowsMarkup = cart.items.map((item, index) => {
      const image = item.image
        ? `<a href="${escapeHtml(item.url)}" class="rb-cart-drawer__media"><img src="${escapeHtml(cartImageUrl(item.image, 360))}" alt="${escapeHtml(item.product_title)}" width="360" height="360" loading="lazy" fetchpriority="low"></a>`
        : '<span class="rb-cart-drawer__media"></span>';
      const variant = item.variant_title && item.variant_title !== 'Default Title'
        ? `<span>${escapeHtml(item.variant_title)}</span>` : '';
      const planName = item.selling_plan_allocation?.selling_plan?.name;
      const plan = planName ? `<span data-rb-cart-plan-label>${escapeHtml(planName)}</span>` : '';
      return `<article class="rb-cart-drawer__item" data-cart-line-key="${escapeHtml(item.key)}" data-cart-variant-id="${escapeHtml(item.variant_id || item.id || '')}">
        <button class="rb-cart-drawer__swipe-remove" type="button" data-rb-cart-quantity="0" data-key="${escapeHtml(item.key)}">Delete</button>
        <div class="rb-cart-drawer__item-main">
          <label class="rb-cart-drawer__select" aria-label="Select ${escapeHtml(item.product_title)}">
            <input type="checkbox" data-rb-cart-select data-key="${escapeHtml(item.key)}" ${selectedKeys.has(item.key) ? 'checked' : ''}>
            <span></span>
          </label>
          ${image}
          <div class="rb-cart-drawer__item-copy">
            <a href="${escapeHtml(item.url)}"><strong>${escapeHtml(item.product_title)}</strong></a>
            ${variant}${plan}
          </div>
          <div class="rb-cart-drawer__item-controls">
            <b>${formatMoney(item.final_line_price, cart.currency)}</b>
            <div class="rb-cart-drawer__quantity" aria-label="Quantity">
              <button type="button" data-rb-cart-quantity="${Math.max(0, item.quantity - 1)}" data-line="${index + 1}" data-key="${escapeHtml(item.key)}" aria-label="${item.quantity === 1 ? 'Remove product' : 'Decrease quantity'}">−</button>
              <span>${item.quantity}</span>
              <button type="button" data-rb-cart-quantity="${item.quantity + 1}" data-line="${index + 1}" data-key="${escapeHtml(item.key)}" aria-label="Increase quantity">+</button>
            </div>
          </div>
          ${cartItemPurchaseOptionsMarkup(item, index)}
        </div>
      </article>`;
    }).join('');

    /* Build the new rows off-screen and move already-decoded product images
       into them before the swap. Reusing the same image nodes prevents the
       white image flash Safari shows whenever a selling-plan response changes
       a line key and the cart markup is reconciled. */
    const nextRows = document.createElement('template');
    nextRows.innerHTML = rowsMarkup;
    nextRows.content.querySelectorAll('.rb-cart-drawer__item[data-cart-variant-id]').forEach((row) => {
      const pool = reusableImages.get(row.dataset.cartVariantId || '');
      const reusableImage = pool?.shift();
      const nextImage = row.querySelector('.rb-cart-drawer__media img');
      if (!reusableImage || !nextImage) return;
      reusableImage.alt = nextImage.alt;
      reusableImage.loading = 'eager';
      nextImage.replaceWith(reusableImage);
    });
    cartItems.replaceChildren(nextRows.content);
    cartItems.scrollTop = previousScrollTop;
    if (activeChoice) {
      const row = [...cartItems.querySelectorAll(`[data-cart-variant-id="${CSS.escape(activeChoice.variantId)}"]`)]
        .find((candidate) => candidate.querySelector(`[data-rb-cart-selling-plan][data-selling-plan="${CSS.escape(activeChoice.planId)}"]`));
      row?.querySelector(`[data-rb-cart-selling-plan][data-selling-plan="${CSS.escape(activeChoice.planId)}"]`)
        ?.focus({ preventScroll: true });
    }
    updateSelection();
  };

  const applyOptimisticSellingPlan = (item, sellingPlan, priceCents) => {
    if (!currentCart || !item) return;
    const itemIndex = currentCart.items.findIndex((candidate) => candidate.key === item.key);
    if (itemIndex < 0) return;
    const planData = sellingPlanCatalog[String(item.variant_id || item.id)];
    const planChoice = planData?.plans?.find((plan) => String(plan.id) === String(sellingPlan));
    const quantity = Math.max(1, Number(item.quantity) || 1);
    const previousLinePrice = Number(item.final_line_price || 0);
    const fallbackUnitPrice = quantity ? previousLinePrice / quantity : 0;
    const nextUnitPrice = Number.isFinite(priceCents) ? priceCents : fallbackUnitPrice;
    const nextLinePrice = Math.round(nextUnitPrice * quantity);
    const nextAllocation = sellingPlan
      ? {
          ...(item.selling_plan_allocation || {}),
          selling_plan: {
            ...(item.selling_plan_allocation?.selling_plan || {}),
            id: Number(sellingPlan) || sellingPlan,
            name: planChoice?.name || 'Subscription',
          },
        }
      : null;
    const nextItem = {
      ...item,
      final_price: Math.round(nextUnitPrice),
      final_line_price: nextLinePrice,
      selling_plan_allocation: nextAllocation,
    };
    const delta = nextLinePrice - previousLinePrice;
    const adjustCartAmount = (value) => Number.isFinite(Number(value)) ? Number(value) + delta : value;
    currentCart = {
      ...currentCart,
      items: currentCart.items.map((candidate, index) => index === itemIndex ? nextItem : candidate),
      items_subtotal_price: adjustCartAmount(currentCart.items_subtotal_price),
      original_total_price: adjustCartAmount(currentCart.original_total_price),
      total_price: adjustCartAmount(currentCart.total_price),
    };

    const row = [...cartItems.querySelectorAll('.rb-cart-drawer__item')]
      .find((candidate) => candidate.dataset.cartLineKey === item.key);
    if (row) {
      row.querySelectorAll('[data-rb-cart-selling-plan]').forEach((button) => {
        const selected = String(button.dataset.sellingPlan || '') === String(sellingPlan || '');
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-pressed', String(selected));
      });
      const price = row.querySelector('.rb-cart-drawer__item-controls > b');
      if (price) price.textContent = formatMoney(nextLinePrice, currentCart.currency);
      let planLabel = row.querySelector('[data-rb-cart-plan-label]');
      if (sellingPlan) {
        if (!planLabel) {
          planLabel = document.createElement('span');
          planLabel.dataset.rbCartPlanLabel = '';
          row.querySelector('.rb-cart-drawer__item-copy')?.append(planLabel);
        }
        planLabel.textContent = planChoice?.name || 'Subscription';
      } else {
        planLabel?.remove();
      }
    }
    updateSelection();
  };

  const runSellingPlanUpdateQueue = () => {
    window.clearTimeout(sellingPlanDebounceTimer);
    sellingPlanDebounceTimer = 0;
    if (sellingPlanRunner) return sellingPlanRunner;

    sellingPlanRunner = (async () => {
      let finalJob = null;
      while (pendingSellingPlanUpdates.size) {
        const [jobKey, job] = pendingSellingPlanUpdates.entries().next().value;
        pendingSellingPlanUpdates.delete(jobKey);
        let cart = serverCart || await fetchServerCart();
        let matchIndex = cart?.items?.findIndex((item) => item.key === job.serverKey) ?? -1;
        if (matchIndex < 0 && Number.isInteger(job.lineHint) && cart?.items?.[job.lineHint]
          && cartPurchaseIdentity(cart.items[job.lineHint]) === job.identity) matchIndex = job.lineHint;
        let match = matchIndex >= 0 ? { item: cart.items[matchIndex], index: matchIndex } : findCartPurchaseIdentity(cart, job.identity);
        if (!match) {
          cart = await fetchServerCart();
          matchIndex = cart?.items?.findIndex((item) => item.key === job.serverKey) ?? -1;
          match = matchIndex >= 0 ? { item: cart.items[matchIndex], index: matchIndex } : findCartPurchaseIdentity(cart, job.identity);
        }
        if (!match) throw new Error('This cart item was refreshed. Please choose the option again.');

        const confirmedPlan = String(match.item.selling_plan_allocation?.selling_plan?.id || '');
        if (confirmedPlan !== job.sellingPlan) {
          try {
            cart = await requestCartJson(changeUrl, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
              body: JSON.stringify({
                line: match.index + 1,
                quantity: match.item.quantity,
                selling_plan: job.sellingPlan ? Number(job.sellingPlan) || job.sellingPlan : null,
              }),
            });
          } catch (error) {
            /* Safari can lose a completed mutation response while navigating or
               changing networks. Confirm the server state before treating the
               option as failed, so a successful change is never rolled back. */
            const confirmedCart = await fetchServerCart();
            const confirmedItem = findCartPurchaseIdentity(confirmedCart, job.identity)?.item;
            const recoveredPlan = String(confirmedItem?.selling_plan_allocation?.selling_plan?.id || '');
            if (recoveredPlan !== job.sellingPlan) throw error;
            cart = confirmedCart;
          }
        }
        serverCart = cart;
        finalJob = job;

        /* A newer tap arrived while Shopify was saving. Never paint this older
           response: continue from it server-side while the shopper keeps seeing
           only the most recent optimistic choice. */
        const newerJob = pendingSellingPlanUpdates.get(jobKey);
        if (newerJob && newerJob.revision > job.revision) {
          const nextLine = cart.items?.[match.index];
          const nextMatch = nextLine && cartPurchaseIdentity(nextLine) === job.identity
            ? { item: nextLine, index: match.index }
            : findCartPurchaseIdentity(cart, job.identity);
          if (nextMatch) {
            newerJob.serverKey = nextMatch.item.key;
            newerJob.lineHint = nextMatch.index;
          }
          continue;
        }
        if (!pendingSellingPlanUpdates.size) {
          cartFetchedAt = performance.now();
          renderCart(cart);
        }
      }

      if (finalJob) {
        setNotice(finalJob.sellingPlan ? 'Subscription option applied.' : 'One-time purchase selected.');
        const completedRevision = finalJob.revision;
        window.setTimeout(() => {
          if (!sellingPlanRunner && !pendingSellingPlanUpdates.size && sellingPlanRevision === completedRevision) setNotice('');
        }, 1100);
      }
      return serverCart || currentCart;
    })().catch(async (error) => {
      try {
        const cart = await fetchServerCart();
        renderCart(cart);
      } catch (_) {}
      setNotice(error.message || 'Purchase option could not be changed.', true);
      throw error;
    }).finally(() => {
      sellingPlanRunner = null;
      if (pendingSellingPlanUpdates.size) {
        sellingPlanDebounceTimer = window.setTimeout(() => {
          runSellingPlanUpdateQueue().catch(() => {});
        }, 0);
      }
    });

    return sellingPlanRunner;
  };

  const queueSellingPlanUpdate = (item, sellingPlan, priceCents) => {
    const identity = cartPurchaseIdentity(item);
    const jobKey = item.key || identity;
    const revision = ++sellingPlanRevision;
    const existingJob = pendingSellingPlanUpdates.get(jobKey);
    pendingSellingPlanUpdates.set(jobKey, {
      identity,
      sellingPlan,
      priceCents,
      revision,
      serverKey: existingJob?.serverKey || item.key,
      lineHint: existingJob?.lineHint ?? currentCart?.items?.findIndex((candidate) => candidate.key === item.key),
    });
    applyOptimisticSellingPlan(item, sellingPlan, priceCents);
    setNotice('Updating your purchase option…');
    window.clearTimeout(sellingPlanDebounceTimer);
    if (!sellingPlanRunner) {
      sellingPlanDebounceTimer = window.setTimeout(() => {
        runSellingPlanUpdateQueue().catch(() => {});
      }, 180);
    }
  };

  const flushSellingPlanUpdates = async () => {
    window.clearTimeout(sellingPlanDebounceTimer);
    sellingPlanDebounceTimer = 0;
    while (sellingPlanRunner || pendingSellingPlanUpdates.size) {
      await (sellingPlanRunner || runSellingPlanUpdateQueue());
    }
  };

  /* Shopify renders the current cart with the page. Use that state
     immediately, so opening the drawer never depends on a fresh network
     request. Subsequent reads still reconcile in the background. */
  if (initialCartData?.textContent) {
    try {
      const initialCart = JSON.parse(initialCartData.textContent);
      if (Array.isArray(initialCart?.items)) {
        cartFetchedAt = performance.now();
        serverCart = initialCart;
        currentCart = initialCart;
        initialCart.items.forEach((item) => {
          selectedKeys.add(item.key);
          knownKeys.add(item.key);
        });
        updateSelection();
        const hydrateInitialCart = () => {
          if (!cartUiHydrated && currentCart) renderCart(currentCart);
        };
        if ('requestIdleCallback' in window) window.requestIdleCallback(hydrateInitialCart, { timeout: 2500 });
        else window.setTimeout(hydrateInitialCart, 600);
      }
    } catch (_) {}
  }

  const getCart = async (force = false) => {
    if ((cartUpdateRunner || pendingQuantityUpdates.size || sellingPlanRunner || pendingSellingPlanUpdates.size) && currentCart) {
      if (!force) return currentCart;
      /* Quantity buttons paint optimistically. If another action (for example
         quick-add or checkout) requests an authoritative refresh during that
         short sync window, wait for the queued absolute quantity mutation and
         then read Shopify again. Returning the optimistic snapshot here can
         leave the badge stale after a rapid remove-then-add sequence. */
      try {
        if (cartUpdateRunner) await cartUpdateRunner;
        if (pendingQuantityUpdates.size) await runQuantityUpdateQueue(true);
        if (sellingPlanRunner || pendingSellingPlanUpdates.size) await flushSellingPlanUpdates();
      } catch (_) {}
    }
    if (!force && currentCart && performance.now() - cartFetchedAt < 3500) return currentCart;
    if (cartRequest) return cartRequest;
    cartRequest = requestCartJson(cartJsonUrl, {
      headers: { Accept: 'application/json' },
    }).then((cart) => {
      cartFetchedAt = performance.now();
      serverCart = cart;
      if (!cartUpdateRunner && !pendingQuantityUpdates.size && !sellingPlanRunner && !pendingSellingPlanUpdates.size) renderCart(cart);
      return cart;
    }).catch((error) => {
      if (currentCart) return currentCart;
      throw error;
    }).finally(() => { cartRequest = null; });
    return cartRequest;
  };

  /* Product-page quick-add controls can update Shopify without opening the
     drawer. Expose the existing cart fetch/render path so the badge, cached
     cart and drawer contents reconcile as one operation after that request. */
  const addCartItems = (items) => requestCartJson(addUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    body: JSON.stringify({ items }),
  });
  window.RUBYST_ADD_CART_ITEMS = addCartItems;
  window.RUBYST_REFRESH_CART = () => getCart(true);

  checkoutForm?.addEventListener('submit', async (event) => {
    if (allowCheckoutSubmit) {
      allowCheckoutSubmit = false;
      return;
    }
    const selectedSubscription = currentCart?.items?.find((item) => selectedKeys.has(item.key) && item.selling_plan_allocation);
    if (selectedSubscription && rbRequireSubscriptionLogin(checkoutButton || checkoutForm)) {
      event.preventDefault();
      return;
    }
    if (cartUpdateRunner || pendingQuantityUpdates.size || sellingPlanRunner || pendingSellingPlanUpdates.size) {
      event.preventDefault();
      if (checkoutAwaitingQuantity) return;
      checkoutAwaitingQuantity = true;
      checkoutButton.disabled = true;
      setNotice('Finishing your cart update…');
      try {
        for (let attempt = 0; attempt < 3 && (cartUpdateRunner || pendingQuantityUpdates.size); attempt += 1) {
          await (cartUpdateRunner || runQuantityUpdateQueue(true));
        }
        await flushSellingPlanUpdates();
        if (pendingQuantityUpdates.size) throw new Error('Cart quantity is still synchronizing.');
        if (pendingSellingPlanUpdates.size) throw new Error('Purchase option is still synchronizing.');
        checkoutAwaitingQuantity = false;
        checkoutButton.disabled = false;
        checkoutForm.requestSubmit(checkoutButton);
      } catch (error) {
        checkoutAwaitingQuantity = false;
        checkoutButton.disabled = false;
        if (pendingQuantityUpdates.size) runQuantityUpdateQueue(true);
        if (pendingSellingPlanUpdates.size) runSellingPlanUpdateQueue().catch(() => {});
        setNotice(error.message || 'Your cart is still updating. Please try again.', true);
      }
      return;
    }
    if (!currentCart) {
      event.preventDefault();
      setNotice('Cart details are still loading. Please try again.', true);
      return;
    }
    const unselected = currentCart.items.filter((item) => !selectedKeys.has(item.key));
    if (!selectedKeys.size) {
      event.preventDefault();
      setNotice('Select at least one item before checkout.', true);
      return;
    }
    if (!unselected.length) return;
    event.preventDefault();
    checkoutButton.disabled = true;
    setNotice('Preparing checkout with your selected items. Unselected items will be removed from this cart.');
    try {
      const updates = Object.fromEntries(unselected.map((item) => [item.key, 0]));
      const cart = await requestCartJson(updateUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates })
      });
      serverCart = cart;
      renderCart(cart);
      allowCheckoutSubmit = true;
      checkoutForm.requestSubmit(checkoutButton);
    } catch (error) {
      checkoutButton.disabled = false;
      setNotice(error.message || 'Selected checkout could not be prepared.', true);
    }
  });

  window.addEventListener('pageshow', (event) => {
    allowCheckoutSubmit = false;
    checkoutAwaitingQuantity = false;
    if (checkoutButton) checkoutButton.disabled = false;
    const navigation = performance.getEntriesByType?.('navigation')?.[0];
    if (event.persisted || navigation?.type === 'back_forward') {
      setNotice('');
      getCart(true).catch(() => {});
    }
  });

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('.rb-product-form, .rb-product-card__form, .rb-product-sticky__form, .rb-final__form');
    if (!form) return;
    const submitter = event.submitter;
    const selectedSellingPlan = String(new FormData(form).get('selling_plan') || '');
    if (selectedSellingPlan && rbRequireSubscriptionLogin(submitter || form)) {
      event.preventDefault();
      return;
    }
    if (submitter?.closest('.shopify-payment-button') || submitter?.classList.contains('shopify-payment-button__button')) return;
    event.preventDefault();
    setNotice('');
    if (submitter) submitter.disabled = true;
    openCart(submitter || form);
    setNotice('Adding your product…');
    try {
      await requestCartJson(addUrl, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: new FormData(form)
      });
      await getCart(true);
      setNotice('Added to your cart.');
    } catch (error) {
      setNotice(error.message || 'This product could not be added.', true);
    } finally {
      if (submitter) submitter.disabled = false;
    }
  });

  document.addEventListener('change', (event) => {
    const selected = event.target.closest('[data-rb-cart-select]');
    if (selected) {
      if (selected.checked) selectedKeys.add(selected.dataset.key);
      else selectedKeys.delete(selected.dataset.key);
      updateSelection();
    }
    if (event.target.matches('[data-rb-cart-select-all]') && currentCart) {
      currentCart.items.forEach((item) => event.target.checked ? selectedKeys.add(item.key) : selectedKeys.delete(item.key));
      updateSelection();
    }
  });

  document.addEventListener('click', async (event) => {
    const open = event.target.closest('[data-rb-cart-open]');
    const close = event.target.closest('[data-rb-cart-close]');
    const expand = event.target.closest('[data-rb-cart-expand]');
    const quantity = event.target.closest('[data-rb-cart-quantity]');
    const sellingPlanChoice = event.target.closest('[data-rb-cart-selling-plan]');
    const originalAdd = event.target.closest('[data-rb-original-add]');
    if (originalAdd) {
      event.preventDefault();
      event.stopPropagation();
      const silentQuickAdd = originalAdd.hasAttribute('data-rb-quick-add');
      const product = window.RUBYST_PRODUCTS?.[originalAdd.dataset.productKey];
      if (!product?.variantId || product.available === false) {
        setNotice('This product is not available yet.', true);
        openCart(originalAdd);
        return;
      }
      const visual = originalAdd.closest('.product-card-cart');
      const visualLabel = visual?.querySelector('.product-card-cart-label');
      const badgeSnapshots = Array.from(document.querySelectorAll('.rb-header__cart-count'), (badge) => [badge, badge.textContent]);
      originalAdd.disabled = true;
      setNotice('');
      badgeSnapshots.forEach(([badge]) => { badge.textContent = String((Number(badge.textContent) || 0) + 1); });
      rbAnimateProductToCart(visual || originalAdd, originalAdd.closest('.product-card')?.querySelector('.product-img-base'));
      if (!silentQuickAdd) {
        openCart(originalAdd);
        setNotice('Adding your product…');
      }
      try {
        await addCartItems([{ id: Number(product.variantId), quantity: 1 }]);
        await getCart(true);
        setNotice('Added to your cart.');
        rbShowCartAddToast();
      } catch (error) {
        badgeSnapshots.forEach(([badge, value]) => { badge.textContent = value; });
        if (visualLabel) visualLabel.textContent = 'Add to Cart';
        getCart(true).catch(() => {});
        setNotice(error.message || 'This product could not be added.', true);
        rbShowCartAddToast(error.message || 'This product could not be added.', true);
        if (!silentQuickAdd) openCart(originalAdd);
      } finally {
        originalAdd.disabled = false;
      }
      return;
    }
    if (open) {
      event.preventDefault();
      const motionSource = window.RUBYST_CART_MOTION_SOURCE || open;
      window.RUBYST_CART_MOTION_SOURCE = null;
      openCart(motionSource);
      getCart().catch(() => setNotice('The cart could not be loaded. Please tap again.', true));
    }
    if (close) closeCart(event);
    if (expand && performance.now() >= suppressHandleClickUntil) closeCart(event);
    if (sellingPlanChoice && currentCart) {
      event.preventDefault();
      const row = sellingPlanChoice.closest('[data-cart-line-key]');
      const key = row?.dataset.cartLineKey || '';
      const fallbackLine = Number(sellingPlanChoice.dataset.line);
      const item = currentCart.items.find((candidate) => candidate.key === key) || currentCart.items[fallbackLine - 1];
      if (!item) return;
      const sellingPlan = sellingPlanChoice.dataset.sellingPlan || '';
      if (sellingPlan && rbRequireSubscriptionLogin(sellingPlanChoice)) return;
      const currentPlan = String(item.selling_plan_allocation?.selling_plan?.id || '');
      const queuedPlan = pendingSellingPlanUpdates.get(item.key || cartPurchaseIdentity(item))?.sellingPlan;
      if ((queuedPlan === sellingPlan) || (queuedPlan === undefined && currentPlan === sellingPlan)) return;
      const optimisticPrice = Number(sellingPlanChoice.dataset.priceCents);
      queueSellingPlanUpdate(item, sellingPlan, optimisticPrice);
      return;
    }
    if (!event.target.closest('.rb-cart-drawer__item')) {
      rubystCartDrawer.querySelectorAll('.rb-cart-drawer__item.is-swiped').forEach((item) => item.classList.remove('is-swiped'));
    }
    if (quantity) {
      event.preventDefault();
      setNotice('');
      const key = quantity.dataset.key;
      const line = Number(quantity.dataset.line) || Math.max(1, currentCart?.items.findIndex((item) => item.key === key) + 1);
      const requestedQuantity = Math.max(0, Number(quantity.dataset.rbCartQuantity) || 0);
      const previousCart = currentCart;
      const item = previousCart?.items[line - 1];
      if (!item) return;

      /* Make quantity controls feel native: paint the requested quantity or
         removal immediately, while Shopify is updated in the background. */
      const unitPrice = item.quantity ? item.final_line_price / item.quantity : 0;
      const nextItem = { ...item, quantity: requestedQuantity, final_line_price: Math.round(unitPrice * requestedQuantity) };
      const nextItems = requestedQuantity === 0
        ? previousCart.items.filter((_, index) => index !== line - 1)
        : previousCart.items.map((cartItem, index) => index === line - 1 ? nextItem : cartItem);
      renderCart({
        ...previousCart,
        items: nextItems,
        item_count: Math.max(0, previousCart.item_count - item.quantity + requestedQuantity),
        total_price: Math.max(0, previousCart.total_price - item.final_line_price + nextItem.final_line_price),
      });
      queueCartQuantity(item, requestedQuantity);
    }
  });

  rubystCartDrawer.addEventListener('touchstart', (event) => {
    const touch = event.touches[0];
    const itemMain = event.target.closest('.rb-cart-drawer__item-main');
    if (itemMain) swipeStarts.set(itemMain, { x: touch.clientX, y: touch.clientY });
  }, { passive: true });

  rubystCartDrawer.addEventListener('touchend', (event) => {
    const touch = event.changedTouches[0];
    const itemMain = event.target.closest('.rb-cart-drawer__item-main');
    if (itemMain) {
      const start = swipeStarts.get(itemMain);
      if (start && Math.abs(touch.clientX - start.x) > Math.abs(touch.clientY - start.y)) {
        const item = itemMain.closest('.rb-cart-drawer__item');
        if (touch.clientX - start.x < -48) item.classList.add('is-swiped');
        if (touch.clientX - start.x > 38) item.classList.remove('is-swiped');
      }
    }
  }, { passive: true });

  expandButton?.addEventListener('pointerdown', (event) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    const sheet = rubystCartDrawer.querySelector('.rb-cart-drawer__sheet');
    if (!sheet) return;
    cartDrag = {
      id: event.pointerId,
      startY: event.clientY,
      startHeight: sheet.getBoundingClientRect().height,
      height: sheet.getBoundingClientRect().height,
      moved: false,
    };
    rubystCartDrawer.classList.add('is-dragging');
    try { expandButton.setPointerCapture?.(event.pointerId); } catch (_) {}
    event.preventDefault();
  });

  expandButton?.addEventListener('pointermove', (event) => {
    if (!cartDrag || cartDrag.id !== event.pointerId) return;
    const distance = Math.max(0, event.clientY - cartDrag.startY);
    cartDrag.distance = distance;
    cartDrag.moved ||= distance > 6;
    rubystCartDrawer.style.setProperty('--rb-cart-drag-y', `${distance}px`);
    event.preventDefault();
  });

  const finishCartDrag = (event) => {
    if (!cartDrag || cartDrag.id !== event.pointerId) return;
    const shouldDismiss = (cartDrag.distance || 0) >= Math.min(180, window.innerHeight * .16);
    if (cartDrag.moved) suppressHandleClickUntil = performance.now() + 380;
    cartDrag = null;
    rubystCartDrawer.style.removeProperty('--rb-cart-drag-y');
    rubystCartDrawer.classList.remove('is-dragging');
    try { expandButton.releasePointerCapture?.(event.pointerId); } catch (_) {}
    if (shouldDismiss) closeCart(event);
  };

  expandButton?.addEventListener('pointerup', finishCartDrag);
  expandButton?.addEventListener('pointercancel', finishCartDrag);

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && !rubystCartDrawer.hidden) closeCart(cartMotionSource);
  });
}

/* RUBYST account drawer: this is a fast theme-level view over Shopify's
   customer account. Authentication and protected data remain on Shopify. */
const rubystAccountDrawer = document.querySelector('[data-rb-account-drawer]');
if (rubystAccountDrawer) {
  const accountHandle = rubystAccountDrawer.querySelector('[data-rb-account-expand]');
  const accountSheet = rubystAccountDrawer.querySelector('.rb-account-drawer__sheet');
  const nativeAccount = rubystAccountDrawer.querySelector('[data-rb-native-account]');
  const subscriptionLoginNote = rubystAccountDrawer.querySelector('[data-rb-subscription-login-note]');
  const accountLanguage = rubystAccountDrawer.querySelector('[data-rb-account-language]');
  const accountLanguageToggle = rubystAccountDrawer.querySelector('[data-rb-language-toggle]');
  const accountLanguageOptions = rubystAccountDrawer.querySelector('[data-rb-language-options]');
  const accountLanguageSelect = accountLanguage?.querySelector('[data-language-select]');
  const accountLanguageCurrent = accountLanguage?.querySelector('[data-rb-language-current]');
  let accountCloseTimer = 0;
  let accountOpenFrame = 0;
  let accountOpenAnimationTimer = 0;
  let accountDrag = null;
  let accountHandleClickAfter = 0;
  let accountReturnFocus = null;
  let nativeAccountFitFrame = 0;
  let accountRefreshPromise = null;
  let accountRefreshAt = 0;
  const accountCompletionStorageKey = 'rb-account-completion-pending';
  const accountReturnStorageKey = 'rb-account-return-panel-v1';

  /* Shopify renders its secure account sheet against the 1280px layout
     viewport. On a real phone that would appear as a tiny panel. CSS zoom
     preserves sharp text while fitting the official form to the visual
     viewport; desktop keeps the normal right-drawer geometry. */
  const prepareNativeAccount = () => {
    const accountRoot = nativeAccount?.shadowRoot;
    if (!accountRoot) return false;
    if (!accountRoot.querySelector('[data-rb-account-overrides]')) {
      const style = document.createElement('style');
      style.dataset.rbAccountOverrides = '';
      style.textContent = '.account__menu-region{display:none!important}';
      accountRoot.append(style);
    }
    const loginForm = accountRoot.querySelector('shopify-login-form');
    const loginRoot = loginForm?.shadowRoot;
    if (loginRoot && !loginRoot.querySelector('[data-rb-email-only]')) {
      const style = document.createElement('style');
      style.dataset.rbEmailOnly = '';
      style.textContent = '.login-form__root>slot[name="shop-login"],.login-form__divider{display:none!important}';
      loginRoot.append(style);
    }
    const emailForm = loginRoot?.querySelector('.login-form__email-form');
    if (emailForm && !emailForm.dataset.rbCompletionListener) {
      emailForm.dataset.rbCompletionListener = 'true';
      emailForm.addEventListener('submit', () => {
        try { window.sessionStorage.setItem(accountCompletionStorageKey, '1'); } catch (_) {}
      });
    }
    return true;
  };

  const fitNativeAccountDialog = () => {
    prepareNativeAccount();
    const dialog = nativeAccount?.shadowRoot?.querySelector('[part="dialog"]');
    if (!dialog || !accountSheet) return false;
    const isPhone = document.documentElement.classList.contains('phone-readable-type');
    const sheetRect = accountSheet.getBoundingClientRect();
    const visualScale = isPhone
      ? window.visualViewport?.scale || Math.min(1, (window.screen?.width || window.innerWidth) / Math.max(1, window.innerWidth))
      : 1;
    const visualWidth = Math.max(320, Math.round((window.visualViewport?.width || window.innerWidth) * visualScale));
    const visualHeight = Math.max(480, Math.round((window.visualViewport?.height || window.innerHeight) * visualScale));
    const compensation = isPhone ? 1 / Math.max(.1, visualScale) : 1;

    dialog.style.setProperty('position', 'fixed', 'important');
    dialog.style.setProperty('inset', isPhone
      ? '0 auto auto 0'
      : `${Math.round(sheetRect.top)}px auto auto ${Math.round(sheetRect.left)}px`, 'important');
    dialog.style.setProperty('width', `${isPhone ? visualWidth : Math.round(sheetRect.width)}px`, 'important');
    dialog.style.setProperty('height', `${isPhone ? visualHeight : Math.round(sheetRect.height)}px`, 'important');
    dialog.style.setProperty('max-width', 'none', 'important');
    dialog.style.setProperty('max-height', 'none', 'important');
    dialog.style.setProperty('margin', '0', 'important');
    dialog.style.setProperty('overflow', 'hidden auto', 'important');
    dialog.style.setProperty('zoom', String(compensation), 'important');
    dialog.style.setProperty('transform', 'none', 'important');
    dialog.style.setProperty('transform-origin', '0 0', 'important');
    dialog.style.setProperty('border-radius', isPhone ? '30px 30px 0 0' : '0', 'important');
    dialog.style.setProperty('box-shadow', '0 -22px 70px rgba(20, 11, 8, .28)', 'important');
    dialog.style.setProperty('z-index', '2147483600', 'important');
    return true;
  };

  const queueNativeAccountFit = () => {
    window.cancelAnimationFrame(nativeAccountFitFrame);
    nativeAccountFitFrame = window.requestAnimationFrame(() => {
      fitNativeAccountDialog();
      window.setTimeout(fitNativeAccountDialog, 80);
      window.setTimeout(fitNativeAccountDialog, 360);
      window.setTimeout(fitNativeAccountDialog, 900);
    });
  };

  const showAccountPanel = (name = 'overview', { reveal = false } = {}) => {
    let activePanel = null;
    rubystAccountDrawer.querySelectorAll('[data-rb-account-tab]').forEach((button) => {
      const active = button.dataset.rbAccountTab === name;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));
    });
    rubystAccountDrawer.querySelectorAll('[data-rb-account-panel]').forEach((panel) => {
      const active = panel.dataset.rbAccountPanel === name;
      panel.hidden = !active;
      if (active) activePanel = panel;
    });
    if (reveal && activePanel) {
      requestAnimationFrame(() => activePanel?.scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        block: 'start',
      }));
    }
  };

  const refreshAccountData = ({ force = false } = {}) => {
    if (!window.RUBYST_CUSTOMER_STATE?.signedIn) return Promise.resolve(false);
    if (accountRefreshPromise) return accountRefreshPromise;
    if (!force && Date.now() - accountRefreshAt < 5000) return Promise.resolve(false);
    accountRefreshAt = Date.now();
    const activeName = rubystAccountDrawer.querySelector('[data-rb-account-tab].is-active')?.dataset.rbAccountTab || 'overview';
    const url = new URL(window.location.href);
    url.hash = '';
    url.searchParams.set('_rb_account_refresh', Date.now().toString(36));
    accountRefreshPromise = fetch(url.href, {
      method: 'GET',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'text/html' },
    }).then((response) => {
      if (!response.ok) throw new Error(`Account refresh failed (${response.status})`);
      return response.text();
    }).then((html) => {
      const freshDocument = new DOMParser().parseFromString(html, 'text/html');
      const freshDrawer = freshDocument.querySelector('[data-rb-account-drawer]');
      if (!freshDrawer) return false;
      const selectors = [
        '[data-rb-account-profile]',
        '[data-rb-account-completion]',
        '[data-rb-account-live="overview"]',
        '[data-rb-account-live="orders"]',
        '[data-rb-account-live="addresses"]',
        '[data-rb-account-live="profile"]',
        '[data-rb-account-footer]',
      ];
      selectors.forEach((selector) => {
        const current = rubystAccountDrawer.querySelector(selector);
        const fresh = freshDrawer.querySelector(selector);
        if (current && fresh) {
          current.replaceWith(fresh.cloneNode(true));
          return;
        }
        if (current && !fresh) {
          current.remove();
          return;
        }
        if (!current && fresh && selector === '[data-rb-account-completion]') {
          const language = rubystAccountDrawer.querySelector('[data-rb-account-language]');
          language?.before(fresh.cloneNode(true));
        }
      });
      showAccountPanel(activeName);
      return true;
    }).catch(() => false).finally(() => {
      accountRefreshPromise = null;
    });
    return accountRefreshPromise;
  };

  const saveAccountReturnPanel = (panel) => {
    try {
      window.sessionStorage.setItem(accountReturnStorageKey, JSON.stringify({ panel, savedAt: Date.now() }));
    } catch (_) {}
  };

  const accountLanguageLabels = new Map([
    ['en-US', 'EN'], ['es', 'ES'], ['fr', 'FR'], ['ar', 'AR'], ['zh-Hans', '简体'], ['zh-Hant', '繁體'],
  ]);
  const syncAccountLanguage = () => {
    const value = accountLanguageSelect?.value || 'en-US';
    if (accountLanguageCurrent) accountLanguageCurrent.textContent = accountLanguageLabels.get(value) || 'EN';
    accountLanguage?.querySelectorAll('[data-rb-language-option]').forEach((button) => {
      button.setAttribute('aria-pressed', String(button.dataset.rbLanguageOption === value));
    });
  };
  const setAccountLanguageOpen = (open) => {
    accountLanguage?.classList.toggle('is-open', open);
    accountLanguageToggle?.setAttribute('aria-expanded', String(open));
    accountLanguageOptions?.setAttribute('aria-hidden', String(!open));
    if (accountLanguageOptions) accountLanguageOptions.inert = !open;
  };
  setAccountLanguageOpen(false);
  syncAccountLanguage();
  accountLanguageSelect?.addEventListener('change', syncAccountLanguage);
  window.addEventListener('rubyst:languagechange', syncAccountLanguage);

  const setAccountExpanded = (expanded) => {
    rubystAccountDrawer.classList.toggle('is-expanded', expanded);
    rubystAccountDrawer.classList.remove('is-dragging');
    rubystAccountDrawer.style.removeProperty('--rb-account-sheet-height');
    accountHandle?.setAttribute('aria-expanded', String(expanded));
    const handleLabel = expanded ? 'Collapse my account' : 'Expand my account';
    accountHandle?.setAttribute('aria-label', window.RubystI18n?.translate?.(handleLabel) || handleLabel);
  };

  const closeAccount = ({ restoreFocus = true, source = null, fromNative = false } = {}) => {
    if (rubystAccountDrawer.hidden) return;
    const nativeDialog = nativeAccount?.shadowRoot?.querySelector('[part="dialog"]');
    if (!fromNative && nativeDialog?.open) {
      nativeAccount.shadowRoot.querySelector('#close-button,.account__close')?.click();
      return;
    }
    window.cancelAnimationFrame(accountOpenFrame);
    window.clearTimeout(accountOpenAnimationTimer);
    rubystAccountDrawer.classList.remove('is-opening');
    rubystAccountDrawer.classList.remove('is-native-auth-active');
    document.documentElement.classList.remove('rb-native-account-open');
    window.clearTimeout(accountCloseTimer);
    if (document.documentElement.classList.contains('phone-readable-type')) {
      ['--rb-motion-x', '--rb-motion-y', '--rb-motion-scale', '--rb-motion-radius'].forEach((property) => rubystAccountDrawer.style.removeProperty(property));
    } else {
      rbSetMorphTarget(rubystAccountDrawer, accountSheet, source, accountReturnFocus);
    }
    rubystAccountDrawer.classList.add('is-closing');
    document.documentElement.classList.remove('rb-account-open');
    setAccountExpanded(false);
    setAccountLanguageOpen(false);
    accountCloseTimer = window.setTimeout(() => {
      rubystAccountDrawer.hidden = true;
      rubystAccountDrawer.classList.remove('is-closing');
      if (restoreFocus && accountReturnFocus?.isConnected) accountReturnFocus.focus({ preventScroll: true });
    }, 520);
  };

  nativeAccount?.addEventListener('open', () => {
    rubystAccountDrawer.classList.add('is-native-auth-active');
    document.documentElement.classList.add('rb-native-account-open');
    queueNativeAccountFit();
  });
  nativeAccount?.addEventListener('close', () => {
    rubystAccountDrawer.classList.remove('is-native-auth-active');
    document.documentElement.classList.remove('rb-native-account-open');
    let awaitingAccountCompletion = false;
    try { awaitingAccountCompletion = window.sessionStorage.getItem(accountCompletionStorageKey) === '1'; } catch (_) {}
    if (awaitingAccountCompletion) {
      window.setTimeout(() => window.location.reload(), 80);
      return;
    }
    if (!rubystAccountDrawer.hidden) closeAccount({ restoreFocus: true, fromNative: true });
  });
  window.addEventListener('resize', queueNativeAccountFit, { passive: true });
  window.visualViewport?.addEventListener('resize', queueNativeAccountFit, { passive: true });

  const openAccount = (trigger, { reason = '', panel = 'overview' } = {}) => {
    document.querySelector('[data-rb-cart-drawer]:not([hidden]) [data-rb-cart-close]')?.click();
    window.clearTimeout(accountCloseTimer);
    rubystAccountDrawer.classList.remove('is-closing');
    accountReturnFocus = trigger || document.activeElement;
    rubystAccountDrawer.hidden = false;
    const isPhoneAccount = document.documentElement.classList.contains('phone-readable-type');
    if (isPhoneAccount) {
      ['--rb-motion-x', '--rb-motion-y', '--rb-motion-scale', '--rb-motion-radius'].forEach((property) => rubystAccountDrawer.style.removeProperty(property));
      rubystAccountDrawer.classList.add('is-opening');
      document.documentElement.classList.add('rb-account-open');
    } else {
      rbSetMorphTarget(rubystAccountDrawer, accountSheet, trigger, accountReturnFocus);
    }
    if (isPhoneAccount) {
      window.cancelAnimationFrame(accountOpenFrame);
      accountOpenFrame = window.requestAnimationFrame(() => {
        if (rubystAccountDrawer.hidden) return;
        setAccountExpanded(false);
        setAccountLanguageOpen(false);
        syncAccountLanguage();
        showAccountPanel(panel);
        if (subscriptionLoginNote) subscriptionLoginNote.hidden = reason !== 'subscription';
        rubystAccountDrawer.querySelector('.rb-account-drawer__body')?.scrollTo({ top: 0 });
        rubystAccountDrawer.querySelector('[data-rb-account-close]')?.focus({ preventScroll: true });
      });
      window.clearTimeout(accountOpenAnimationTimer);
      accountOpenAnimationTimer = window.setTimeout(() => rubystAccountDrawer.classList.remove('is-opening'), 540);
    } else {
      setAccountExpanded(false);
      setAccountLanguageOpen(false);
      syncAccountLanguage();
      showAccountPanel(panel);
      if (subscriptionLoginNote) subscriptionLoginNote.hidden = reason !== 'subscription';
      rubystAccountDrawer.querySelector('.rb-account-drawer__body')?.scrollTo({ top: 0 });
      void rubystAccountDrawer.offsetWidth;
      document.documentElement.classList.add('rb-account-open');
      rubystAccountDrawer.querySelector('[data-rb-account-close]')?.focus({ preventScroll: true });
    }
    refreshAccountData().then((updated) => {
      if (!updated || rubystAccountDrawer.hidden) return;
      const currentPanel = rubystAccountDrawer.querySelector('[data-rb-account-tab].is-active')?.dataset.rbAccountTab || panel;
      showAccountPanel(currentPanel);
    });
  };
  window.RUBYST_OPEN_ACCOUNT_FOR_SUBSCRIPTION = (trigger) => openAccount(trigger, { reason: 'subscription' });

  try {
    const accountCompletion = rubystAccountDrawer.querySelector('[data-rb-account-completion]');
    const signedInProfile = rubystAccountDrawer.querySelector('.rb-account-profile-card');
    const awaitingAccountCompletion = window.sessionStorage.getItem(accountCompletionStorageKey) === '1';
    if (awaitingAccountCompletion) {
      window.sessionStorage.removeItem(accountCompletionStorageKey);
      if (signedInProfile && accountCompletion) {
        window.setTimeout(() => {
          const trigger = document.querySelector('[data-rb-account-open]') || document.body;
          openAccount(trigger);
          accountCompletion.scrollIntoView({ block: 'nearest' });
        }, 260);
      }
    }
  } catch (_) {}

  const restoreReturnedAccountPanel = () => {
    let returnState = null;
    try { returnState = JSON.parse(window.sessionStorage.getItem(accountReturnStorageKey) || 'null'); } catch (_) {}
    if (!returnState?.panel || Date.now() - returnState.savedAt > 2 * 60 * 60 * 1000) {
      if (returnState) {
        try { window.sessionStorage.removeItem(accountReturnStorageKey); } catch (_) {}
      }
      return;
    }
    try { window.sessionStorage.removeItem(accountReturnStorageKey); } catch (_) {}
    window.setTimeout(() => {
      refreshAccountData({ force: true }).finally(() => {
        const trigger = document.querySelector('[data-rb-account-open]') || document.body;
        openAccount(trigger, { panel: returnState.panel });
        window.setTimeout(() => showAccountPanel(returnState.panel, { reveal: true }), 80);
      });
    }, 120);
  };

  window.addEventListener('pageshow', (event) => {
    refreshAccountData({ force: event.persisted });
    restoreReturnedAccountPanel();
  });
  window.addEventListener('focus', () => refreshAccountData(), { passive: true });
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) refreshAccountData();
  });

  document.addEventListener('click', (event) => {
    const open = event.target.closest('[data-rb-account-open]');
    const close = event.target.closest('[data-rb-account-close]');
    const expand = event.target.closest('[data-rb-account-expand]');
    const tab = event.target.closest('[data-rb-account-tab]');
    const support = event.target.closest('[data-rb-account-support]');
    const cartOpen = event.target.closest('[data-rb-cart-open]');
    const languageToggle = event.target.closest('[data-rb-language-toggle]');
    const languageOption = event.target.closest('[data-rb-language-option]');
    const accountReturn = event.target.closest('[data-rb-account-return]');

    if (accountReturn) saveAccountReturnPanel(accountReturn.dataset.rbAccountReturn || 'overview');

    if (open) {
      event.preventDefault();
      openAccount(open);
      return;
    }
    if (close) {
      event.preventDefault();
      closeAccount({ source: event });
      return;
    }
    if (tab) {
      event.preventDefault();
      const panelName = tab.dataset.rbAccountTab;
      showAccountPanel(panelName, { reveal: true });
      if (panelName === 'addresses') {
        refreshAccountData({ force: true }).then((updated) => {
          if (updated && rubystAccountDrawer.querySelector('[data-rb-account-tab="addresses"].is-active')) {
            showAccountPanel('addresses', { reveal: true });
          }
        });
      }
      return;
    }
    if (languageToggle) {
      event.preventDefault();
      setAccountLanguageOpen(!accountLanguage?.classList.contains('is-open'));
      return;
    }
    if (languageOption && accountLanguageSelect) {
      event.preventDefault();
      accountLanguageSelect.value = languageOption.dataset.rbLanguageOption;
      accountLanguageSelect.dispatchEvent(new Event('change', { bubbles: true }));
      syncAccountLanguage();
      return;
    }
    if (support) {
      event.preventDefault();
      closeAccount({ restoreFocus: false, source: event });
      window.setTimeout(() => {
        document.querySelector('.support-mascot, [data-phone-support], .phone-control-support')?.click();
      }, 220);
      return;
    }
    if (cartOpen && !rubystAccountDrawer.hidden) closeAccount({ restoreFocus: false, source: event });
    if (expand && performance.now() >= accountHandleClickAfter) {
      setAccountExpanded(!rubystAccountDrawer.classList.contains('is-expanded'));
    }
  });

  accountHandle?.addEventListener('pointerdown', (event) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    if (!accountSheet) return;
    const height = accountSheet.getBoundingClientRect().height;
    accountDrag = { id: event.pointerId, startY: event.clientY, startHeight: height, height, moved: false };
    rubystAccountDrawer.classList.add('is-dragging');
    try { accountHandle.setPointerCapture?.(event.pointerId); } catch (_) {}
    event.preventDefault();
  });

  accountHandle?.addEventListener('pointermove', (event) => {
    if (!accountDrag || accountDrag.id !== event.pointerId) return;
    const distance = accountDrag.startY - event.clientY;
    const nextHeight = Math.max(window.innerHeight * .34, Math.min(window.innerHeight, accountDrag.startHeight + distance));
    accountDrag.height = nextHeight;
    accountDrag.moved ||= Math.abs(distance) > 6;
    rubystAccountDrawer.style.setProperty('--rb-account-sheet-height', `${nextHeight}px`);
    event.preventDefault();
  });

  const finishAccountDrag = (event) => {
    if (!accountDrag || accountDrag.id !== event.pointerId) return;
    const { height, moved } = accountDrag;
    accountDrag = null;
    if (moved) accountHandleClickAfter = performance.now() + 380;
    if (moved && height < window.innerHeight * .44) closeAccount({ source: event });
    else setAccountExpanded(height >= window.innerHeight * .84);
    try { accountHandle.releasePointerCapture?.(event.pointerId); } catch (_) {}
  };

  accountHandle?.addEventListener('pointerup', finishAccountDrag);
  accountHandle?.addEventListener('pointercancel', finishAccountDrag);

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape' || rubystAccountDrawer.hidden) return;
    if (accountLanguage?.classList.contains('is-open')) {
      setAccountLanguageOpen(false);
      accountLanguageToggle?.focus({ preventScroll: true });
      return;
    }
    closeAccount({ source: accountReturnFocus });
  });
}

/* Native details/summary menus should never leave a dropdown stranded open.
   A tap outside closes it immediately, and Escape closes every open dropdown. */
const rbOpenDropdownDetailsSelector = 'details.header-store-hub[open], details.rb-mobile-menu[open], details.rb-header__language[open]';
document.addEventListener('pointerdown', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;
  document.querySelectorAll(rbOpenDropdownDetailsSelector).forEach((details) => {
    if (!details.contains(target)) details.open = false;
  });
}, { passive: true });

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;
  document.querySelectorAll(rbOpenDropdownDetailsSelector).forEach((details) => { details.open = false; });
});

/* CSS background URLs otherwise download even when their sections are many
   screens below the fold. Activate them near the real viewport instead. */
document.querySelectorAll('[data-rb-deferred-background]').forEach((section) => {
  const activate = () => section.classList.add('rb-section-art-ready');
  if (!('IntersectionObserver' in window)) {
    activate();
    return;
  }
  const observer = new IntersectionObserver((entries) => {
    if (!entries.some((entry) => entry.isIntersecting)) return;
    observer.disconnect();
    activate();
  }, { rootMargin: '180px 0px', threshold: .01 });
  observer.observe(section);
});

/* Explicit page-exit controls (Back / Done / Cancel / Close links) keep the
   same spatial contract as dialogs. Checkout stays immediate; ordinary local
   links only save entry geometry and do not delay navigation. */
try {
  const entryMotion = JSON.parse(sessionStorage.getItem('rb-page-entry-motion') || 'null');
  sessionStorage.removeItem('rb-page-entry-motion');
  if (!document.documentElement.classList.contains('phone-readable-type') && entryMotion && Number.isFinite(entryMotion.x) && Number.isFinite(entryMotion.y)) {
    document.documentElement.style.setProperty('--rb-page-motion-x', `${entryMotion.x}px`);
    document.documentElement.style.setProperty('--rb-page-motion-y', `${entryMotion.y}px`);
    document.documentElement.style.setProperty('--rb-page-motion-scale', String(entryMotion.scale || .9));
    document.documentElement.style.setProperty('--rb-page-motion-radius', entryMotion.radius || '28px');
    document.documentElement.classList.add('rb-page-motion', 'rb-page-entering');
    requestAnimationFrame(() => requestAnimationFrame(() => {
      document.documentElement.classList.remove('rb-page-entering');
      window.setTimeout(() => document.documentElement.classList.remove('rb-page-motion'), 520);
    }));
  }
} catch (_) {}

const rbPageMotionForLink = (link) => {
  const rect = link.getBoundingClientRect();
  const pointX = rect.left + rect.width / 2;
  const pointY = rect.top + rect.height / 2;
  return {
    x: (pointX - innerWidth / 2) * .42,
    y: (pointY - innerHeight / 2) * .42,
    scale: Math.max(.82, Math.min(.94, Math.min(rect.width / Math.max(innerWidth, 1), rect.height / Math.max(innerHeight, 1)) + .78)),
    radius: getComputedStyle(link).borderRadius || '28px',
  };
};

const rbStorePageEntry = (motion) => {
  try { sessionStorage.setItem('rb-page-entry-motion', JSON.stringify(motion)); } catch (_) {}
};

/* Normal internal links navigate immediately. Only their geometry is saved;
   the destination page consumes it and performs the opening half of the morph. */
document.addEventListener('click', (event) => {
  if (event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
  if (!link || link.target || link.hasAttribute('download') || link.matches('.article-back,[data-rb-page-exit]')) return;
  if (link.matches('[data-rb-cart-open],[data-rb-account-open],[data-rb-support-open],[data-rb-desktop-zoom],[data-rb-desktop-records-open]')) return;
  const destination = new URL(link.href, location.href);
  if (destination.origin !== location.origin || destination.href === location.href || destination.pathname.startsWith('/checkout')) return;
  if (destination.hash && destination.pathname === location.pathname && destination.search === location.search) return;
  rbStorePageEntry(rbPageMotionForLink(link));
}, { capture: true });

document.addEventListener('click', (event) => {
  if (event.defaultPrevented || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
  if (!link || link.target || link.hasAttribute('download')) return;
  const label = `${link.getAttribute('aria-label') || ''} ${link.textContent || ''}`.trim();
  if (!link.matches('.article-back,[data-rb-page-exit]') && !/^(back|done|cancel|close|return)\b/i.test(label)) return;
  const destination = new URL(link.href, location.href);
  if (destination.origin !== location.origin || destination.href === location.href || destination.hash && destination.pathname === location.pathname && destination.search === location.search) return;

  /* Native navigation is faster and more predictable on touch devices. The
     desktop morph remains unchanged, while phone exit controls no longer sit
     behind an artificial 500ms delay. */
  if (document.documentElement.classList.contains('phone-readable-type')) return;

  event.preventDefault();
  const motion = rbPageMotionForLink(link);
  document.documentElement.style.setProperty('--rb-page-motion-x', `${motion.x}px`);
  document.documentElement.style.setProperty('--rb-page-motion-y', `${motion.y}px`);
  document.documentElement.style.setProperty('--rb-page-motion-scale', String(motion.scale));
  document.documentElement.style.setProperty('--rb-page-motion-radius', motion.radius);
  document.documentElement.classList.add('rb-page-motion', 'rb-page-leaving');
  rbStorePageEntry(motion);
  window.setTimeout(() => location.assign(destination.href), 500);
});

/* Movable phone cart -------------------------------------------------------
   The real Shopify cart trigger is detached from the narrow header and kept
   fixed to the visual viewport. It can be dragged anywhere, follows scrolling,
   and docks into the nearest side after one minute of inactivity. The edge
   reminder remains interactive: tap it or pull inward to reveal the cart. */
(() => {
  const root = document.documentElement;
  if (!root.classList.contains('phone-readable-type') || !document.body?.classList.contains('rb-original-layout')) return;

  const cartButton = document.querySelector('.site-header .header-paypal-cart [data-rb-cart-open]')
    || document.querySelector('.phone-control-cart [data-rb-cart-open]');
  const floatingCart = cartButton?.closest('.header-paypal-cart,.phone-control-cart');
  if (!cartButton || !floatingCart || document.querySelector('[data-rb-floating-cart]')) return;

  const requestedDelay = Number(new URLSearchParams(location.search).get('cart-hide-ms'));
  const hideDelay = Number.isFinite(requestedDelay) && requestedDelay >= 200 && requestedDelay <= 60000
    ? requestedDelay
    : 60000;
  const edgeGap = 24;
  let hideTimer = 0;
  let motionTimer = 0;
  let hintTimer = 0;
  let resizeFrame = 0;
  let suppressClickUntil = 0;
  let drag = null;
  let hintDrag = null;
  let mode = 'visible';
  let dockEdge = 'right';
  let visiblePosition = { x: 0, y: 0 };

  const hint = document.createElement('button');
  hint.type = 'button';
  hint.className = 'rb-floating-cart-hint is-right';
  hint.dataset.rbFloatingCartHint = '';
  hint.hidden = true;
  hint.innerHTML = '<span class="rb-floating-cart-hint__arrow" aria-hidden="true">→</span><strong dir="auto"></strong>';

  floatingCart.classList.add('rb-floating-cart', 'is-initializing');
  floatingCart.dataset.rbFloatingCart = '';
  floatingCart.setAttribute('aria-label', 'Movable shopping cart');
  document.body.append(floatingCart, hint);

  const viewport = () => ({
    width: document.documentElement.clientWidth || window.innerWidth,
    height: window.innerHeight || document.documentElement.clientHeight,
  });

  const cartSize = () => {
    const rect = floatingCart.getBoundingClientRect();
    return { width: rect.width || 190, height: rect.height || 101 };
  };

  const clampVisiblePosition = (x, y) => {
    const view = viewport();
    const size = cartSize();
    return {
      x: Math.max(0, Math.min(view.width - size.width, x)),
      y: Math.max(0, Math.min(view.height - size.height, y)),
    };
  };

  const paintPosition = (x, y) => {
    floatingCart.style.setProperty('left', `${Math.round(x)}px`, 'important');
    floatingCart.style.setProperty('top', `${Math.round(y)}px`, 'important');
    floatingCart.style.setProperty('right', 'auto', 'important');
    floatingCart.style.setProperty('bottom', 'auto', 'important');
  };

  const defaultPosition = () => {
    const view = viewport();
    const size = cartSize();
    const topControlsBottom = [...document.querySelectorAll('.site-header,.promo-bar,.article-nav')]
      .map((node) => node.getBoundingClientRect())
      .filter((rect) => rect.bottom > 0 && rect.top < view.height / 2)
      .reduce((bottom, rect) => Math.max(bottom, rect.bottom), 0);
    return clampVisiblePosition(view.width - size.width - edgeGap, topControlsBottom + 12);
  };

  const languageCode = () => String(
    document.documentElement.dataset.language
      || document.querySelector('[data-language-select]')?.value
      || document.documentElement.lang
      || 'en-US'
  ).toLowerCase();

  const hintCopy = () => {
    const language = languageCode();
    if (language.includes('zh-hans') || language.startsWith('zh-cn')) return '购物车在这里 · 点击或拉出';
    if (language.includes('zh-hant') || language.startsWith('zh-tw')) return '購物車在這裡 · 點擊或拉出';
    if (language.startsWith('es')) return 'Carrito aquí · toca o desliza';
    if (language.startsWith('fr')) return 'Panier ici · touchez ou tirez';
    if (language.startsWith('ar')) return 'السلة هنا · اضغط أو اسحب';
    return 'Cart here · tap or pull';
  };

  const updateHintCopy = () => {
    const copy = hintCopy();
    hint.querySelector('strong').textContent = copy;
    hint.setAttribute('aria-label', copy);
  };

  const updateHintPosition = () => {
    const view = viewport();
    const size = cartSize();
    const centreY = Math.max(70, Math.min(view.height - 70, visiblePosition.y + size.height / 2));
    hint.style.setProperty('top', `${Math.round(centreY)}px`);
    hint.classList.toggle('is-left', dockEdge === 'left');
    hint.classList.toggle('is-right', dockEdge === 'right');
    hint.querySelector('.rb-floating-cart-hint__arrow').textContent = dockEdge === 'left' ? '←' : '→';
  };

  const hideHint = () => {
    window.clearTimeout(hintTimer);
    hint.classList.remove('is-visible');
    hintTimer = window.setTimeout(() => {
      if (!hint.classList.contains('is-visible')) hint.hidden = true;
    }, 300);
  };

  const armHideTimer = () => {
    window.clearTimeout(hideTimer);
    if (mode !== 'visible') return;
    hideTimer = window.setTimeout(() => window.RUBYST_FLOATING_CART?.dock(), hideDelay);
  };

  const dock = () => {
    if (mode !== 'visible' || drag) return;
    if (root.classList.contains('rb-cart-open') || root.classList.contains('rb-account-open') || root.classList.contains('support-chat-is-open')) {
      armHideTimer();
      return;
    }

    window.clearTimeout(hideTimer);
    window.clearTimeout(motionTimer);
    const view = viewport();
    const size = cartSize();
    dockEdge = visiblePosition.x + size.width / 2 < view.width / 2 ? 'left' : 'right';
    mode = 'docking';
    updateHintCopy();
    updateHintPosition();
    cartButton.tabIndex = -1;
    floatingCart.setAttribute('aria-hidden', 'true');
    floatingCart.classList.remove('is-docked', 'is-revealing', 'is-dragging');
    floatingCart.classList.add('is-docking');

    requestAnimationFrame(() => {
      paintPosition(dockEdge === 'left' ? -size.width - 12 : view.width + 12, visiblePosition.y);
      hintTimer = window.setTimeout(() => {
        hint.hidden = false;
        requestAnimationFrame(() => hint.classList.add('is-visible'));
      }, 430);
      motionTimer = window.setTimeout(() => {
        if (mode !== 'docking') return;
        mode = 'docked';
        floatingCart.classList.remove('is-docking');
        floatingCart.classList.add('is-docked');
      }, 660);
    });
  };

  const reveal = () => {
    if (mode === 'visible') {
      armHideTimer();
      return;
    }

    window.clearTimeout(hideTimer);
    window.clearTimeout(motionTimer);
    hideHint();
    mode = 'revealing';
    cartButton.removeAttribute('tabindex');
    floatingCart.removeAttribute('aria-hidden');
    floatingCart.classList.remove('is-docked', 'is-docking');
    floatingCart.classList.add('is-revealing');
    requestAnimationFrame(() => requestAnimationFrame(() => paintPosition(visiblePosition.x, visiblePosition.y)));
    motionTimer = window.setTimeout(() => {
      if (mode !== 'revealing') return;
      mode = 'visible';
      floatingCart.classList.remove('is-revealing');
      armHideTimer();
    }, 660);
  };

  const reset = () => {
    window.clearTimeout(hideTimer);
    window.clearTimeout(motionTimer);
    drag = null;
    hintDrag = null;
    mode = 'visible';
    dockEdge = 'right';
    hideHint();
    cartButton.removeAttribute('tabindex');
    floatingCart.removeAttribute('aria-hidden');
    floatingCart.classList.remove('is-docked', 'is-docking', 'is-revealing', 'is-dragging');
    floatingCart.classList.add('is-initializing');
    visiblePosition = defaultPosition();
    paintPosition(visiblePosition.x, visiblePosition.y);
    requestAnimationFrame(() => floatingCart.classList.remove('is-initializing'));
    armHideTimer();
  };

  visiblePosition = defaultPosition();
  floatingCart.classList.add('is-positioned');
  paintPosition(visiblePosition.x, visiblePosition.y);
  requestAnimationFrame(() => floatingCart.classList.remove('is-initializing'));
  updateHintCopy();

  floatingCart.addEventListener('pointerdown', (event) => {
    if (mode !== 'visible' || event.pointerType === 'mouse' && event.button !== 0) return;
    window.clearTimeout(hideTimer);
    drag = {
      id: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      originX: visiblePosition.x,
      originY: visiblePosition.y,
      moved: false,
    };
    try { floatingCart.setPointerCapture(event.pointerId); } catch (_) {}
  });

  floatingCart.addEventListener('pointermove', (event) => {
    if (!drag || event.pointerId !== drag.id) return;
    const deltaX = event.clientX - drag.startX;
    const deltaY = event.clientY - drag.startY;
    if (!drag.moved && Math.hypot(deltaX, deltaY) < 8) return;
    drag.moved = true;
    floatingCart.classList.add('is-dragging');
    visiblePosition = clampVisiblePosition(drag.originX + deltaX, drag.originY + deltaY);
    paintPosition(visiblePosition.x, visiblePosition.y);
    event.preventDefault();
  });

  const finishCartDrag = (event) => {
    if (!drag || event.pointerId !== drag.id) return;
    const moved = drag.moved;
    try { floatingCart.releasePointerCapture(event.pointerId); } catch (_) {}
    drag = null;
    floatingCart.classList.remove('is-dragging');
    if (moved) {
      suppressClickUntil = performance.now() + 450;
      event.preventDefault();
    }
    armHideTimer();
  };

  floatingCart.addEventListener('pointerup', finishCartDrag);
  floatingCart.addEventListener('pointercancel', finishCartDrag);
  floatingCart.addEventListener('lostpointercapture', (event) => {
    if (drag && event.pointerId === drag.id) finishCartDrag(event);
  });
  floatingCart.addEventListener('dragstart', (event) => event.preventDefault());
  floatingCart.addEventListener('click', (event) => {
    if (performance.now() < suppressClickUntil) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }
    armHideTimer();
  }, true);

  hint.addEventListener('pointerdown', (event) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    hintDrag = { id: event.pointerId, x: event.clientX, y: event.clientY };
    try { hint.setPointerCapture(event.pointerId); } catch (_) {}
  });

  hint.addEventListener('pointermove', (event) => {
    if (!hintDrag || event.pointerId !== hintDrag.id) return;
    const deltaX = event.clientX - hintDrag.x;
    const inwardPull = dockEdge === 'left' ? deltaX > 28 : deltaX < -28;
    if (!inwardPull) return;
    hintDrag = null;
    event.preventDefault();
    reveal();
  });

  const finishHintDrag = (event) => {
    if (!hintDrag || event.pointerId !== hintDrag.id) return;
    try { hint.releasePointerCapture(event.pointerId); } catch (_) {}
    hintDrag = null;
  };
  hint.addEventListener('pointerup', finishHintDrag);
  hint.addEventListener('pointercancel', finishHintDrag);
  hint.addEventListener('click', reveal);

  document.addEventListener('click', (event) => {
    if (mode !== 'visible' && event.target.closest('[data-rb-original-add]')) reveal();
  }, { capture: true });

  const keepInsideViewport = () => {
    resizeFrame = 0;
    if (root.classList.contains('rb-chat-native-viewport')
      || root.classList.contains('support-chat-is-open')
      || root.classList.contains('rb-chat-closing-viewport')) return;
    const next = clampVisiblePosition(visiblePosition.x, visiblePosition.y);
    visiblePosition = next;
    if (mode === 'visible' || mode === 'revealing') paintPosition(next.x, next.y);
    else {
      const view = viewport();
      const size = cartSize();
      paintPosition(dockEdge === 'left' ? -size.width - 12 : view.width + 12, next.y);
      updateHintPosition();
    }
  };
  const queueViewportUpdate = () => {
    if (resizeFrame) return;
    resizeFrame = requestAnimationFrame(keepInsideViewport);
  };
  window.addEventListener('resize', queueViewportUpdate, { passive: true });
  window.visualViewport?.addEventListener('resize', queueViewportUpdate, { passive: true });
  window.addEventListener('rubyst:languagechange', updateHintCopy);
  window.addEventListener('pageshow', () => requestAnimationFrame(() => requestAnimationFrame(reset)));
  window.addEventListener('popstate', () => requestAnimationFrame(reset));

  let overlayWasOpen = root.classList.contains('rb-cart-open')
    || root.classList.contains('rb-account-open')
    || root.classList.contains('support-chat-is-open');
  const overlayObserver = new MutationObserver(() => {
    const overlayOpen = root.classList.contains('rb-cart-open')
      || root.classList.contains('rb-account-open')
      || root.classList.contains('support-chat-is-open');
    if (overlayWasOpen && !overlayOpen
      && !root.classList.contains('rb-chat-native-viewport')
      && !root.classList.contains('rb-chat-closing-viewport')) {
      requestAnimationFrame(reset);
    }
    overlayWasOpen = overlayOpen;
  });
  overlayObserver.observe(root, { attributes: true, attributeFilter: ['class'] });
  window.addEventListener('pagehide', () => {
    window.clearTimeout(hideTimer);
    window.clearTimeout(motionTimer);
    window.clearTimeout(hintTimer);
    overlayObserver.disconnect();
  }, { once: true });

  window.RUBYST_FLOATING_CART = {
    delay: hideDelay,
    dock,
    reveal,
    reset,
    getState: () => ({ mode, edge: dockEdge, position: { ...visiblePosition } }),
  };
  armHideTimer();
})();
